import { FC } from 'react';

import AlibabaSvg from '~/svg/alibaba.svg';
import ByteDanceSvg from '~/svg/bytedance.svg';
import FluxSvg from '~/svg/flux.svg';
import GoogleSvg from '~/svg/google_small.svg';
import HailuoSvg from '~/svg/Hailuo.svg';
import KlingSvg from '~/svg/Kling.svg';
import LTXVSvg from '~/svg/ltx.svg';
import PixverseSvg from '~/svg/pixverse.svg';
import WanSvg from '~/svg/wan.svg';
import XAISvg from '~/svg/xai.svg';
export type AspectRatioType =
  | '16:9'
  | '9:16'
  | '1:1'
  | '4:3'
  | '3:4'
  | '21:9'
  | '9:21'
  | '3:2'
  | '2:3';
export type DurationType =
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | '11'
  | '12'
  | '13'
  | '14'
  | '15'
  | '16'
  | '18'
  | '20'
  | '25'
  | '30'
  | '4s'
  | '5s'
  | '6s'
  | '8s'
  | '9s';
// '768p' and '2k' are MiniMax H3's own two output tiers (its API takes the
// literal strings '768P' / '2K'); every other value maps to a provider's
// conventional ladder. '360p' is Gemini Omni 1.1 Flash's draft tier (640x360,
// verified) — a third of the 720p price for quick iteration.
export type ResolutionType =
  | '360p'
  | '480p'
  | '720p'
  | '768p'
  | '1080p'
  | '2k'
  | '4k';
export type InputType = 'image' | 'text';
export type ReferenceImageType = 'character' | 'object' | 'style';

export interface ReferenceImageConfig {
  /** Maximum number of reference images allowed */
  maxImages: number;
  /** Types of references this model supports */
  supportedTypes: ReferenceImageType[];
  /** API field mapping for different providers */
  fieldMapping: {
    /** For Kling O1 elements array */
    elementsField?: string;
    /** For Kling O1 style images */
    imageUrlsField?: string;
    /** For Veo 3.1 reference images */
    referenceImagesField?: string;
  };
}

export interface ReferenceAudioConfig {
  /** Maximum number of reference audio files the API accepts. */
  maxAudios: number;
  /** Combined length cap across all reference audio, in seconds. */
  maxTotalSeconds: number;
  /**
   * Audio cannot be provided alone — the API requires at least one image
   * reference alongside it (MiniMax H3).
   */
  requiresImageReference?: boolean;
  /**
   * Per-clip duration bounds. MiniMax H3 rejects any clip outside [2, 15]s,
   * which the combined-total cap alone doesn't catch — a single 1s clip passes
   * the total check and still fails at the provider.
   */
  minClipSeconds?: number;
  maxClipSeconds?: number;
  /**
   * Container formats the API can ingest, sniffed from the file's magic bytes.
   * The picker accepts more than this (m4a/aac/ogg/flac/webm), so anything not
   * listed here must be rejected or converted before submission.
   */
  acceptedFormats?: Array<'wav' | 'mp3'>;
  /** Per-clip byte cap. */
  maxBytesPerClip?: number;
}

export interface ReferenceVideoConfig {
  /** Maximum number of reference video clips the API accepts. */
  maxVideos: number;
  /** Per-clip duration bounds, in seconds. */
  minClipSeconds: number;
  maxClipSeconds: number;
  /** Combined length cap across all reference videos, in seconds. */
  maxTotalSeconds: number;
  /** Per-clip byte cap — enforced client-side at upload and again at submit. */
  maxBytesPerClip: number;
  /**
   * The reference video is the SOURCE being edited, not a motion reference:
   * the output mirrors its duration and no separate output length is chosen
   * (Gemini Omni Flash). Everything else treats the clip as guidance for
   * motion, camera work and style while the user still picks the output
   * length.
   */
  isEditSource?: boolean;
  /**
   * The provider bills the reference video's own duration on top of the
   * generated output, at the same per-second rate.
   *
   * MiniMax states it outright ("reference video is billed by its input
   * duration at the same per-second rate as the output"), and ModelArk's
   * Seedance token formula counts input video seconds the same way
   * (`(input seconds + output seconds) x W x H x fps / 1024`, at the lower
   * $6.40/M video-input rate). Without this a 15s reference on a 4s output
   * would be sold at a fifth of what it costs to render.
   *
   * Unset for `isEditSource` models, which already price off the input
   * duration alone (see getOmniVideoEditCredits).
   */
  billsInputSeconds?: boolean;
  /**
   * The provider re-prices the WHOLE job once a reference video is attached:
   * `rate × (output + max(output, reference))` seconds, where the reference
   * term is the clips' combined length capped at `maxTotalSeconds` — instead
   * of the plain per-second output rate (Kling 4.0 on WaveSpeed). The value is
   * credits per counted second at the base resolution; the resolution
   * multiplier still applies on top.
   *
   * Mutually exclusive with `billsInputSeconds`: getReferenceVideoCredits
   * returns the difference between this price and the plain output price, so
   * callers keep adding it to getVideoModelCredits exactly as before.
   */
  countedSecondsCreditsPerSecond?: number;
}

/**
 * Reference inputs priced as TOKENS pooled across every reference file, rather
 * than as the reference clip's seconds (`billsInputSeconds`). fal's MiniMax H3
 * Max reference endpoint is the first model here billed this way.
 *
 * The two schemes are mutually exclusive: a model sets one or the other, never
 * both, or its references are charged twice.
 */
export interface ReferenceTokenPricing {
  /** Tokens included at no charge, pooled across images, clips and audio. */
  freeTokens: number;
  /** Credits per 1,000 billable tokens. */
  creditsPerThousandTokens: number;
  /** An image costs `(width * height) / imageTokenDivisor` tokens. */
  imageTokenDivisor: number;
  /**
   * Tokens per second of reference VIDEO, keyed by the resolution the output
   * is generated at — NOT the reference clip's own resolution, which fal says
   * outright does not affect the price.
   */
  videoTokensPerSecond: Partial<Record<ResolutionType, number>>;
}

export interface VideoModelOptions {
  duration?: DurationType[];
  aspectRatio: AspectRatioType[];
  inputType: InputType[];
  supportsTailImage?: boolean;
  tailImageFieldName?: string;
  imageFieldName?: string;
  generatesAudio?: boolean;
  /** Whether this is a motion control model (requires video input) */
  isMotionControl?: boolean;
  /** Additional parameters to pass to the model API */
  additionalParams?: Record<string, unknown>;
  /** Whether this model supports reference images for consistent characters/objects */
  supportsReferenceImages?: boolean;
  /** Configuration for reference image support */
  referenceImageConfig?: ReferenceImageConfig;
  /** Whether this model REQUIRES reference images (should only show when references are uploaded) */
  requiresReferenceImages?: boolean;
  /**
   * Whether this model accepts reference audio (Seedance 2.0 text/reference
   * variants, MiniMax H3 reference). The audio guides the generated
   * soundtrack — speech, singing, or music.
   */
  supportsReferenceAudio?: boolean;
  /** Configuration for reference audio support */
  referenceAudioConfig?: ReferenceAudioConfig;
  /**
   * Whether this model accepts a reference video input. Two different
   * behaviours share the flag, told apart by `referenceVideoConfig.isEditSource`:
   *
   *   Gemini Omni Flash  — the clip IS the source being edited; the output
   *                        mirrors its duration. Sent inline as base64, so
   *                        uploads are size-capped hard.
   *   Seedance 2.0/2.5,  — the clip is a MOTION/camera/style reference passed
   *   MiniMax H3           by URL alongside reference images and audio, while
   *                        the user still picks the output length.
   */
  supportsReferenceVideo?: boolean;
  /** Configuration for reference video support */
  referenceVideoConfig?: ReferenceVideoConfig;
  /**
   * Reference inputs billed as pooled tokens rather than as reference seconds.
   * Mutually exclusive with `referenceVideoConfig.billsInputSeconds`.
   */
  referenceTokenPricing?: ReferenceTokenPricing;
  /**
   * For a base (text/image) model that can't take references itself but has a
   * dedicated reference-to-video sibling, the `id` of that sibling. Lets the
   * Video Generator surface "Add media" and switch to it when references are
   * applied — for siblings the model-path heuristic can't resolve (e.g. Grok
   * shares one model id; Kling O3 Pro's reference lives under o3/standard).
   */
  referenceSiblingId?: string;
  /** Credits charged per second of video duration. If set, credits = duration × creditsPerSecond */
  creditsPerSecond?: number;
  /** Available resolution options for this model */
  resolution?: ResolutionType[];
  /**
   * Which duration/resolution to preselect. Defaults to the first entry in the
   * arrays above, which is how every model expressed its default before these
   * existed — set them only when the preferred default is not the one that
   * should lead the list, so the picker can stay in a sensible order (Omni 1.1
   * offers a 3s floor and a 360p draft tier, neither of which should be the
   * default). Resolve them with getDefaultDurationForModel /
   * getDefaultResolutionForModel, never by indexing the arrays.
   */
  defaultDuration?: DurationType;
  defaultResolution?: ResolutionType;
  /** Credits multiplier for specific resolutions (e.g., { '4k': 2 } means 4K costs 2× base) */
  resolutionCreditsMultiplier?: Partial<Record<ResolutionType, number>>;
  /** Whether this model allows users to toggle audio generation on/off */
  supportsAudioToggle?: boolean;
  /**
   * This model ALWAYS bakes audio into its output and exposes no audio
   * parameter at all, so `generateAudio: false` is silently ignored.
   *
   * Opt-in per model on purpose: `generatesAudio` is set on ~37 entries that
   * DO accept a `generate_audio` field, so it cannot be used to infer this.
   * Only set it when the provider's schema genuinely has no audio control.
   *
   * Consequences: no audio toggle (picking "off" would be a lie), no audio-off
   * discount (there is no cheaper silent mode to bill for), and the video
   * orchestrator mutes the clip so the model's own dialogue and score don't
   * play underneath the generated voiceover.
   */
  alwaysGeneratesAudio?: boolean;
  /** Credits multiplier when audio is disabled (e.g., 0.5 means half price without audio). Defaults to 1 (no discount) */
  audioDisabledCreditsMultiplier?: number;
  /** Maximum prompt length in characters. Defaults to DEFAULT_VIDEO_PROMPT_MAX_LENGTH */
  maxPromptLength?: number;
}

// MiniMax H3 runs on MiniMax's own platform (@/utils/minimaxVideo.ts), which
// bills per generated second by output tier: 2K $0.13/s, 768P $0.08/s
// (platform.minimax.io/docs/guides/pricing-paygo). `creditsPerSecond` below is
// the 2K rate (subsidised — 2K costs ~6.5 credits/s at our credit value) and
// 768P rides the same 0.6 price ratio, so both tiers carry the same margin.
// 768P leads the list, so it is what `getDefaultResolutionForModel` preselects.
//
// Deliberately NOT billed through: reference audio is free, and the first 5
// reference images are free ($0.04 each beyond that — a 9-image reference run
// costs ~$0.16 extra, absorbed rather than surfaced as a variable price).
const MINIMAX_H3_CREDITS_PER_SECOND = 5;
const MINIMAX_H3_RESOLUTIONS: ResolutionType[] = ['768p', '2k'];
const MINIMAX_H3_RESOLUTION_MULTIPLIER: Partial<
  Record<ResolutionType, number>
> = { '768p': 0.6 };
/** H3 accepts any integer 4-15s; 4s leads, so it is the preselected default. */
const MINIMAX_H3_DURATIONS: DurationType[] = [
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
];
/** Default display price: 4s at the 768P default (4 × 5 × 0.6). */
const MINIMAX_H3_DEFAULT_CREDITS = 12;

// MiniMax H3 Max is a SEPARATE model from MiniMax H3 above: a variant fal
// post-trained for speed, served only by fal (`minimax/h3-max/*`), never by
// MiniMax's own platform API — hence `isMiniMaxVideoModel` matching
// `minimax/h3/` and not the bare vendor prefix. It renders 480P/768P only;
// anyone who needs 2K or video editing still goes to standard H3.
//
// fal's list price is $0.05/s at 480P and $0.08/s at 768P (launch promos have
// run at half and a quarter of that; we price off list, not off a promo). At
// the house rate of cost ÷ $0.02/credit that is 2.5 and 4 credits/s; 480P is
// rounded DOWN to 2 to keep the ladder a clean 2/4 — a deliberate ~20%
// subsidy on the draft tier only.
//
// The base rate is the 768P tier because that is also fal's own default, so a
// caller who omits `resolution` is billed exactly what renders — which is why
// H3 Max needs no entry in the `billedResolution` special case in
// /api/generate-video (MiniMax H3 does: its base rate is the 2K tier).
const MINIMAX_H3_MAX_CREDITS_PER_SECOND = 4;
const MINIMAX_H3_MAX_RESOLUTIONS: ResolutionType[] = ['768p', '480p'];
const MINIMAX_H3_MAX_RESOLUTION_MULTIPLIER: Partial<
  Record<ResolutionType, number>
> = { '480p': 0.5 };
/** fal accepts any integer 5-15s and defaults to 5, which leads the list. */
const MINIMAX_H3_MAX_DURATIONS: DurationType[] = [
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
];
/** Default display price: 5s at the 768P default (5 × 4). */
const MINIMAX_H3_MAX_DEFAULT_CREDITS = 20;

/**
 * H3 Max reference clips. Same caps as standard H3's reference endpoint
 * (3 clips, 2-15s each, 15s combined) but WITHOUT `billsInputSeconds`: on fal
 * a reference clip is not charged at the output's per-second rate, it is
 * charged as TOKENS — see MINIMAX_H3_MAX_REFERENCE_TOKENS below. Setting both
 * would bill every reference twice.
 *
 * fal caps reference images + videos + audio at 12 FILES combined, on top of
 * the per-kind caps. The catalogue has no field for a combined cap, so the
 * per-kind caps (9 + 3 + 3) can add up past it — the endpoint rejects that
 * itself, which is the same posture as the other reference models here.
 */
const MINIMAX_H3_MAX_REFERENCE_VIDEO: ReferenceVideoConfig = {
  maxVideos: 3,
  minClipSeconds: 2,
  maxClipSeconds: 15,
  maxTotalSeconds: 15,
  maxBytesPerClip: 20 * 1024 * 1024,
};

/**
 * H3 Max reference-to-video charges reference inputs separately from the
 * output, as tokens pooled across every image, clip and audio file: the first
 * 4,096 are free, then $0.02 per 1,000. At our $0.02/credit that is exactly
 * ONE credit per 1,000 billable tokens — full cost recovery with no
 * conversion loss (fal's own pricing page, 2026-09-02).
 *
 * An image costs `(width * height) / 1024` tokens, so the allowance covers
 * four 1024x1024 references or one 2048x2048. A reference VIDEO costs by its
 * duration and by the tier being GENERATED at — the clip's own resolution is
 * explicitly irrelevant — at 2,886 tokens/s when generating 480p and 7,459.2
 * when generating 768p (fal's table: 5s at 768p is 37,296 tokens).
 *
 * This is why the reference endpoint is worth billing properly rather than
 * absorbing: a single 15s clip on a 768p render is 111,888 tokens, about
 * $2.16 — over five times the $0.40 the 5-second output itself costs.
 *
 * AUDIO is pooled into the same budget by fal but no per-clip formula is
 * published, so audio tokens are absorbed. Bounded by the 3-clip / 15s cap and
 * almost certainly small beside video, but it IS an unpriced input — worth
 * revisiting if fal documents the rate.
 */
const MINIMAX_H3_MAX_REFERENCE_TOKENS: ReferenceTokenPricing = {
  freeTokens: 4096,
  creditsPerThousandTokens: 1,
  imageTokenDivisor: 1024,
  videoTokensPerSecond: { '480p': 2886, '768p': 7459.2 },
};

/**
 * Reference video limits, per family. All three take the clips as URLs
 * alongside reference images and audio, and treat them as guidance for motion,
 * camera work and pacing rather than as footage to edit.
 *
 * MiniMax H3 (platform.minimax.io video-generation v2): up to 3 clips, 2-15s
 * each and 15s combined, MP4/MOV. A reference_video/_image/_audio role can
 * never be mixed with first_frame/last_frame — the roles are exclusive.
 */
const MINIMAX_H3_REFERENCE_VIDEO: ReferenceVideoConfig = {
  maxVideos: 3,
  minClipSeconds: 2,
  maxClipSeconds: 15,
  maxTotalSeconds: 15,
  maxBytesPerClip: 20 * 1024 * 1024,
  billsInputSeconds: true,
};

/**
 * Gemini Omni 1.1 Flash — the stable successor to `gemini-omni-flash-preview`.
 *
 * Same Vertex interactions API and the same inline-base64 delivery, but with
 * the Veo-style creative controls the preview lacked: a 360p draft tier, 1080p
 * and 4K upscaling, first/last-frame interpolation, and 3-second minimum
 * clips. All of it probed live against Vertex on 2026-08-28 — every tier came
 * back at its exact nominal size (360p 640x360, 720p 1280x720, 1080p
 * 1920x1080, 4k 3840x2160), 24 fps with a native stereo AAC track.
 *
 * The Vertex model id is `gemini-omni-1.1-flash-preview`, NOT the
 * `gemini-omni-1.1-flash` published in the Gemini Developer API docs — that
 * name is rejected on Vertex with "Unsupported model interaction". Re-probe
 * before assuming a docs model id works here.
 *
 * Do NOT send `response_format.delivery: 'uri'`: Vertex accepts the field and
 * then FAILS the interaction (probed). Even a 4K clip comes back inline
 * (8.5MB), so the base64 path covers every tier.
 */
export const GEMINI_OMNI_11_MODEL_ID = 'gemini-omni-1.1-flash-preview';

/**
 * Gemini Omni is metered by TOKEN, not by second: $17.50 per 1M video-output
 * tokens, charged at 1931 / 5792 / 8688 / 17376 tokens per second of
 * 360p / 720p / 1080p / 4K video (Vertex AI pricing, re-checked 2026-09-10).
 * That works out to $0.0338 / $0.1014 / $0.1520 / $0.3041 per second, i.e.
 * 1.69 / 5.07 / 7.60 / 15.20 credits per second at our $0.02/credit.
 *
 * 6 credits/s at 720p is cost recovery with the house round-up, and the extra
 * ~18% over the video-output tokens covers the two meters this rate does NOT
 * model: prompt input ($1.50/1M, and 5792 tokens per second of any INPUT
 * video on the edit/extend paths) and the model's own text/reasoning output
 * ($9.00/1M). The multipliers are Google's exact token ratios relative to
 * 720p, so every tier recovers identically instead of the expensive tiers
 * compounding a loss.
 *
 * This replaced a subsidised 3 credits/s (~59% recovery) on 2026-09-10.
 */
export const GEMINI_OMNI_11_CREDITS_PER_SECOND = 6;
export const GEMINI_OMNI_11_RESOLUTION_MULTIPLIER: Partial<
  Record<ResolutionType, number>
> = { '360p': 1 / 3, '1080p': 1.5, '4k': 3 };

/** The full ladder, in order; see GEMINI_OMNI_11_DEFAULT_RESOLUTION. */
export const GEMINI_OMNI_11_RESOLUTIONS: ResolutionType[] = [
  '360p',
  '720p',
  '1080p',
  '4k',
];
/** 720p is Google's own default, and the draft tier should not lead. */
export const GEMINI_OMNI_11_DEFAULT_RESOLUTION: ResolutionType = '720p';

/**
 * The API renders 3-10s (3s verified live; above 10s the interaction FAILS
 * rather than clamping).
 */
const GEMINI_OMNI_11_DURATIONS: DurationType[] = [
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
];
/** Matches the preview entry rather than dropping to the new 3s floor. */
const GEMINI_OMNI_11_DEFAULT_DURATION: DurationType = '4';

/** Default display price: 4s at the 720p default (4 x 6). */
const GEMINI_OMNI_11_DEFAULT_CREDITS = 24;

/**
 * Veo, per second of output video. Google's Vertex AI list prices (re-checked
 * 2026-09-10; the "$X / 1 count" rows are per second — the Gemini API page
 * publishes the identical figures as "per second in USD"):
 *
 *                     720p    1080p    4K
 *   Veo 3.1   +audio  $0.40   $0.40   $0.60
 *             silent  $0.20   $0.20   $0.40
 *   Veo 3.1   Fast    $0.10   $0.12   $0.30
 *             silent  $0.08   $0.10   $0.25
 *   Veo 3.1   Lite    $0.05   $0.08     --
 *
 * At $0.02/credit the base rates below are those prices exactly, and each
 * resolution multiplier is Google's own ratio relative to 720p. Before
 * 2026-09-10 the whole family was subsidised (6 / 3 / 2 credits per second,
 * 30-80% recovery) with 4K multipliers that did not match any real ratio.
 *
 * The audio-off multipliers are Google's discount for the silent SKU, which
 * differs per family: Veo 3.1 halves, Fast only takes ~15-20% off. 0.85 is
 * used for Fast because one multiplier has to cover all three tiers
 * (0.80 / 0.83 / 0.83) without ever dipping under cost. Veo 3.1's silent 4K
 * tier is the one cell that cannot be expressed — Google discounts it by
 * a third, not a half — so it recovers 75%; see getVideoModelCredits, which
 * applies a single audio multiplier across every resolution.
 *
 * Veo 3.1 Lite has NO audio multiplier on purpose: googleCloudVideo.ts cannot
 * send generateAudio for Lite (the model rejects it), so every Lite render is
 * billed by Google at the audio-inclusive rate and must be charged as such.
 */
const VEO_31_CREDITS_PER_SECOND = 20;
const VEO_31_RESOLUTION_MULTIPLIER: Partial<Record<ResolutionType, number>> = {
  '4k': 1.5,
};
const VEO_31_AUDIO_OFF_MULTIPLIER = 0.5;
/** Default display price: 8s at 720p (8 x 20). */
const VEO_31_DEFAULT_CREDITS = 160;

const VEO_31_FAST_CREDITS_PER_SECOND = 5;
const VEO_31_FAST_RESOLUTION_MULTIPLIER: Partial<
  Record<ResolutionType, number>
> = { '1080p': 1.2, '4k': 3 };
const VEO_31_FAST_AUDIO_OFF_MULTIPLIER = 0.85;
/** Default display price: 8s at 720p (8 x 5). */
const VEO_31_FAST_DEFAULT_CREDITS = 40;

const VEO_31_LITE_CREDITS_PER_SECOND = 2.5;
const VEO_31_LITE_RESOLUTION_MULTIPLIER: Partial<
  Record<ResolutionType, number>
> = { '1080p': 1.6 };
/** Default display price: 8s at 720p (8 x 2.5). */
const VEO_31_LITE_DEFAULT_CREDITS = 20;

/**
 * Seedance 2.0 (ModelArk `reference_video` role / WaveSpeed `reference_videos`):
 * up to 3 clips of 2-15s, matching the family's own 15s output ceiling. The
 * provider's audio-only rejection names them explicitly — "reference_audios
 * requires at least one image ... or reference video" — so a clip also
 * satisfies the reference-audio companion requirement.
 */
const SEEDANCE_20_REFERENCE_VIDEO: ReferenceVideoConfig = {
  maxVideos: 3,
  minClipSeconds: 2,
  maxClipSeconds: 15,
  maxTotalSeconds: 15,
  maxBytesPerClip: 30 * 1024 * 1024,
  billsInputSeconds: true,
};

/**
 * Seedance 2.5 lifts every reference cap (30 images / 10 videos / 10 audio)
 * and stretches clips to the model's 30s one-take window. Capped at 3 clips
 * here regardless: the picker attaches one video at a time, and each extra
 * clip is billed input seconds.
 */
const SEEDANCE_25_REFERENCE_VIDEO: ReferenceVideoConfig = {
  maxVideos: 3,
  minClipSeconds: 2,
  maxClipSeconds: 30,
  maxTotalSeconds: 30,
  maxBytesPerClip: 50 * 1024 * 1024,
  billsInputSeconds: true,
};

// The Dreamina Seedance 2.5 AND 2.0 families run on BytePlus ModelArk
// (@/utils/bytedanceVideo.ts) with a WaveSpeed fallback
// (@/lib/seedance25Fallback.ts) for everything ModelArk refuses — which is any
// input asset showing a real person, i.e. most real footage — plus the tiers
// ModelArk doesn't have at all (all turbo variants; 2.0 Fast/Mini at 1080p/4K).
// ModelArk 2.0 is 30-40% cheaper ($0.15/s vs $0.24/s @720p std; Fast $0.12 vs
// $0.20; Mini $0.08 vs $0.12 — verified against both price lists 2026-08-12).
// Which provider serves a job is only knowable after submitting it, so
// **pricing follows the worst case, WaveSpeed** — nothing here changes:
//
//   ModelArk (token-billed, tokens = seconds × W × H × fps / 1024 at $10.70/M)
//     720p → ~21,825 tokens/s → $0.2335/s      480p → ~9,700 tokens/s → $0.1038/s
//   WaveSpeed (catalogue formula 900_000 × resMult × duration/5 micro-dollars)
//     720p → $0.36/s                            480p → $0.18/s
//
// DELIBERATELY SOLD BELOW COST — do not "correct" this to cost recovery.
//
// The house convention is credits = provider cost ÷ $0.02/credit (Seedance 2.0
// spells it out: $0.24/s -> 12), which for 2.5 would be 18 credits/s at 720p
// and 9 at 480p. Seedance 2.5 is instead priced as a loss-leader at ~2/3 of
// cost, the same posture as Seedance 2.0 Mini (67%) and Fast (60%), to drive
// adoption of the new flagship:
//   720p  12 credits/s = $0.24/s  against a $0.36/s cost
//   480p   6 credits/s = $0.12/s  against a $0.18/s cost  (the usual ×0.5)
// That is a real loss of ~$0.12 per 720p second and ~$0.06 per 480p second
// against WaveSpeed's list price (~10% less while the account discount holds,
// and much less on the jobs ModelArk serves, which cost roughly half).
// Worst single job: a 30s 720p render loses ~$3.60.
//
// NOTE: WaveSpeed charges the formula and nothing else. An earlier revision
// priced in a ~$0.14/job "base fee" that does not exist — it was an artifact of
// reading balance deltas while other generations were settling on the same
// account. Never size these rates from balance deltas unless the account is
// idle; use the catalogue formula.
const SEEDANCE_25_CREDITS_PER_SECOND = 12;
const SEEDANCE_25_RESOLUTIONS: ResolutionType[] = ['480p', '720p'];
const SEEDANCE_25_RESOLUTION_MULTIPLIER: Partial<
  Record<ResolutionType, number>
> = { '480p': 0.5 };
/**
 * Seedance 2.5 accepts any whole second in [4, 30]. A curated ladder keeps the
 * picker usable; `duration[0]` is what the UI preselects.
 */
const SEEDANCE_25_DURATIONS: DurationType[] = [
  '4',
  '5',
  '6',
  '8',
  '10',
  '12',
  '15',
  '20',
  '25',
  '30',
];
/** Default display price: 4s at the 480p default (ceil(4 × 12) × 0.5). */
const SEEDANCE_25_DEFAULT_CREDITS = 24;
// Neither provider caps the prompt: WaveSpeed's schema for every Seedance 2.5
// endpoint has no maxLength, and BytePlus ModelArk's `content.text` doc only
// says "Recommended prompt length: no more than 500 Chinese characters or
// 1,000 English words. Lengthy text will lead to scattered information" — a
// quality note, not a limit (2026-09-22). Third-party template catalogues run
// 12-29K-character prompts on this model, so the cap matches WAN 3.0.
const SEEDANCE_25_MAX_PROMPT_LENGTH = 20000;

// Seedance 2.5 Turbo exists ONLY on WaveSpeed — ModelArk has no turbo/fast/pro
// 2.5 model id at all (probed: all 404) — so these skip the ModelArk attempt
// and submit straight to WaveSpeed. Their formula is
// `(900_000 + (1080p ? 150_000 : 100_000)) × duration/5` micro-dollars, i.e. a
// near-flat $0.20/s at 720p and $0.21/s at 1080p, which makes turbo cheaper
// per second than ModelArk's own standard 2.5 ($0.2335/s at 720p).
// Cost recovery would be 10 credits/s; held at 7 to keep the same ~2/3
// loss-leader posture as the standard tier (see the note above).
const SEEDANCE_25_TURBO_CREDITS_PER_SECOND = 7;
/** Turbo renders 720p/1080p only — 480p is rejected outright. */
const SEEDANCE_25_TURBO_RESOLUTIONS: ResolutionType[] = ['720p', '1080p'];
const SEEDANCE_25_TURBO_RESOLUTION_MULTIPLIER: Partial<
  Record<ResolutionType, number>
> = { '1080p': 1.05 };
/** Default display price: 4s at the 720p default (4 × 7). */
const SEEDANCE_25_TURBO_DEFAULT_CREDITS = 28;

// FLUX 3 (Black Forest Labs) runs on WaveSpeed. Its catalogue formula is
//   0.85 × min(max(ceil(duration), 5), 20) / 5 × (1080p ? 29/17 : 1)  dollars
// i.e. a flat $0.17/s at 720p and $0.29/s at 1080p, with no per-request base
// fee. One price covers all three endpoints — text-to-video, image-to-video,
// and start-end-to-video all share base_price 0.85.
//
// House convention is credits = provider cost ÷ $0.02/credit, which lands on
// 8.5 credits/s at 720p; rounded up to a whole 9 so the rate never sits under
// cost. 1080p rides ×1.7, matching the provider's own 29/17 ≈ 1.706 step
// (9 × 1.7 = 15.3 credits/s = $0.306/s against a $0.29/s cost).
const FLUX_3_CREDITS_PER_SECOND = 9;
/** FLUX 3 renders 720p/1080p only. */
const FLUX_3_RESOLUTIONS: ResolutionType[] = ['720p', '1080p'];
const FLUX_3_RESOLUTION_MULTIPLIER: Partial<Record<ResolutionType, number>> = {
  '1080p': 1.7,
};
/**
 * FLUX 3 accepts any whole second in [5, 20] (the formula ceils and clamps to
 * that window). A curated ladder keeps the picker usable; `duration[0]` is
 * what the UI preselects.
 */
const FLUX_3_DURATIONS: DurationType[] = [
  '5',
  '6',
  '8',
  '10',
  '12',
  '15',
  '20',
];
/** Default display price: 5s at the 720p default (5 × 9). */
const FLUX_3_DEFAULT_CREDITS = 45;
/** The three FLUX 3 endpoints share this aspect-ratio enum. */
const FLUX_3_ASPECT_RATIOS: AspectRatioType[] = [
  '16:9',
  '9:16',
  '1:1',
  '4:3',
  '3:4',
  '21:9',
];

// WAN 3.0 (Alibaba Tongyi Lab, public beta 2026-08-06) runs on WaveSpeed as
// three separate endpoints — text-to-video, image-to-video and a real
// reference-to-video (unlike Seedance, whose reference mode rides the
// text-to-video endpoint behind a `reference_images` signal).
//
// Pricing is a flat per-output-second rate by resolution with no base fee:
// $0.05/s at 480p, $0.10/s at 720p, $0.20/s at 1080p. House convention is
// credits = provider cost ÷ $0.02/credit, which lands exactly on 2.5 / 5 / 10
// credits per second — so 720p is the base rate and the other two tiers are
// clean ×0.5 and ×2 steps with no rounding loss.
const WAN_3_CREDITS_PER_SECOND = 5;
/** WAN 3.0 renders 480p/720p/1080p. `resolution[0]` is the picker default. */
const WAN_3_RESOLUTIONS: ResolutionType[] = ['480p', '720p', '1080p'];
const WAN_3_RESOLUTION_MULTIPLIER: Partial<Record<ResolutionType, number>> = {
  '480p': 0.5,
  '1080p': 2,
};
/**
 * WAN 3.0 accepts any whole second in [2, 30] — the headline feature is the
 * 30-second single-run take. A curated ladder keeps the picker usable;
 * `duration[0]` is what the UI preselects.
 */
const WAN_3_DURATIONS: DurationType[] = [
  '5',
  '8',
  '10',
  '12',
  '15',
  '20',
  '25',
  '30',
];
/** Default display price: 5s at the 720p default (5 × 5). */
const WAN_3_DEFAULT_CREDITS = 25;
/**
 * WAN 3.0 takes `adaptive` plus five fixed ratios. We always send a concrete
 * ratio, so only the fixed five are exposed.
 */
const WAN_3_ASPECT_RATIOS: AspectRatioType[] = [
  '16:9',
  '9:16',
  '1:1',
  '4:3',
  '3:4',
];
/** WAN 3.0 documents a 20,000-character prompt window. */
const WAN_3_MAX_PROMPT_LENGTH = 20000;

// Kling 4.0 (Kuaishou) runs on WaveSpeed as five endpoints under
// `kwaivgi/kling-v4.0/`: text-, image- and reference-to-video here, plus
// video-edit (an engine of the timestamp editor) and video-extend (a tier of
// the Extend tool). Every schema is `additionalProperties: false` and none has
// an audio field — each render comes back with synchronized stereo audio — so
// the payload is built by `buildKling40VideoInput` in @/utils/wavespeedVideo,
// never by the Seedance builder.
//
// Pricing, from the formula on each model page (micro-dollars, 2026-10-24):
//   text / image / reference, no reference video, and extend (new segment only)
//     (1080p ? 235_000 : 157_000) × duration      $0.157/s 720p, $0.235/s 1080p
//   reference WITH reference videos
//     (1080p ? 141_000 : 94_000) × (duration + max(duration, refSeconds))
//   edit (output follows the source)
//     (1080p ? 1.5 : 1) × 94_000 × 2 × sourceSeconds    $0.188/s, $0.282/s
// At the house rate of cost ÷ $0.02/credit, rounded up to a whole credit:
// 8 credits/s at 720p ($0.16 against $0.157) and ×1.5 at 1080p (12/s, $0.24
// against $0.235). No base fee, and reference images and audio are free.
const KLING_40_CREDITS_PER_SECOND = 8;
const KLING_40_RESOLUTIONS: ResolutionType[] = ['720p', '1080p'];
const KLING_40_RESOLUTION_MULTIPLIER: Partial<Record<ResolutionType, number>> =
  { '1080p': 1.5 };
/**
 * Kling 4.0 takes any whole second in [3, 30]. A curated ladder keeps the
 * picker usable; 5s is the provider's own default.
 */
const KLING_40_DURATIONS: DurationType[] = [
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '12',
  '15',
  '20',
  '25',
  '30',
];
const KLING_40_DEFAULT_DURATION: DurationType = '5';
/** Default display price: 5s at the 720p default (5 × 8). */
const KLING_40_DEFAULT_CREDITS = 40;
/** The four ratios every Kling 4.0 endpoint accepts, 21:9 ultrawide included. */
const KLING_40_ASPECT_RATIOS: AspectRatioType[] = [
  '16:9',
  '9:16',
  '1:1',
  '21:9',
];
/** No published prompt cap; matches the other 30-second flagships. */
const KLING_40_MAX_PROMPT_LENGTH = 20000;
/**
 * Image, last frame and in-between keyframes together are at most 10 on the
 * image-to-video endpoint, and the reference endpoint takes 10 images too.
 */
export const KLING_40_MAX_IMAGES = 10;
/**
 * Catalogue path of the keyframes entry. Not a WaveSpeed endpoint: its images
 * are sent to image-to-video as the first frame, the in-between `keyframes`
 * and the last frame.
 */
export const KLING_40_KEYFRAMES_MODEL = 'kwaivgi/kling-v4.0/keyframes-to-video';
/**
 * Reference clips: up to 5, used together for at most 30 seconds (the provider
 * trims past that — refused here instead). The provider bills them as counted
 * seconds, `94_000 × (output + max(output, refSeconds))` micro-dollars at
 * 720p: 4.7 credits per counted second, ×1.5 at 1080p, exact cost.
 */
const KLING_40_REFERENCE_VIDEO: ReferenceVideoConfig = {
  maxVideos: 5,
  minClipSeconds: 3,
  maxClipSeconds: 30,
  maxTotalSeconds: 30,
  maxBytesPerClip: 50 * 1024 * 1024,
  countedSecondsCreditsPerSecond: 4.7,
};

// FLUX 3 Max (Black Forest Labs) runs on fal as three endpoints —
// `blackforestlabs/flux-3-max/{text,image,keyframes}-to-video` — a variant of
// FLUX 3 tuned for prompt adherence and quality at lower latency. List price
// matches FLUX 3 on WaveSpeed: $0.17/s at 720p, $0.29/s at 1080p, per second
// of output, no base fee, same price on all three endpoints.
//
// fal launched it at 75% off ($0.0425/s and $0.0725/s) from Oct 15 to
// Nov 7 2026, and that discount is passed through to users at the house rate
// of cost ÷ $0.02/credit: 2.125 credits/s at 720p, ×1.71 at 1080p (fal's own
// 0.0725/0.0425 ≈ 1.706 step). After the promo the rates fall back to FLUX 3's
// list pricing (9 credits/s, ×1.7) with no deploy needed.
//
// The pricing fields on the catalogue entries are GETTERS, so every read
// re-checks the clock — a warm lambda or an open tab doesn't keep billing the
// promo rate after it ends. The cut-off is the START of Nov 7 UTC (fal doesn't
// say whose midnight ends it): ending a day early only costs users the last
// day of the discount, ending late would bill at a quarter of our cost.
const FLUX_3_MAX_PROMO_ENDS_AT = Date.parse('2026-11-07T00:00:00Z');
const FLUX_3_MAX_PROMO_CREDITS_PER_SECOND = 2.125;
const FLUX_3_MAX_PROMO_RESOLUTION_MULTIPLIER: Partial<
  Record<ResolutionType, number>
> = { '1080p': 1.71 };

/** True while fal's FLUX 3 Max launch discount is running. */
export function isFlux3MaxPromoActive(now: number = Date.now()): boolean {
  return now < FLUX_3_MAX_PROMO_ENDS_AT;
}

/**
 * Pricing fields shared by the three FLUX 3 Max entries. Spread into
 * `options` (and the entry itself for `credits`/`isPromo`) — spreading an
 * object of getters copies the getters' current VALUES, so each entry
 * declares its own getters that delegate here instead.
 */
export const flux3MaxPricing = () => {
  const promo = isFlux3MaxPromoActive();
  const creditsPerSecond = promo
    ? FLUX_3_MAX_PROMO_CREDITS_PER_SECOND
    : FLUX_3_CREDITS_PER_SECOND;
  return {
    promo,
    creditsPerSecond,
    resolutionCreditsMultiplier: promo
      ? FLUX_3_MAX_PROMO_RESOLUTION_MULTIPLIER
      : FLUX_3_RESOLUTION_MULTIPLIER,
    /** Display price: 5s at the 720p default (11 on promo, 45 at list). */
    credits: Math.ceil(5 * creditsPerSecond),
  };
};

/**
 * Keyframes-to-video pins up to 10 images to frame positions of the 24 fps
 * output. The picker has no timeline, so /api/generate-video spaces them
 * evenly from the first frame to the last, in the order they were added.
 */
const FLUX_3_MAX_MAX_KEYFRAMES = 10;
export const FLUX_3_MAX_KEYFRAMES_FPS = 24;

export const videoModelsWithDescription = [
  {
    id: 'veo31-fast-text',
    // Veo runs on Vertex AI (covered by GCP credits). Free users pay the
    // standard credit prices, capped at 720p. Enforced in /api/generate-video.
    freeUserAccess: true,
    freePlanPolicy: { resolutions: ['720p'] as ResolutionType[] },
    title: 'Veo 3.1 Fast',
    description:
      'Fast version of Google Veo 3.1 with quick generation times and up to 4K resolution',
    model: 'veo-3.1-fast-generate-001',
    isNew: false,
    isBest: true,
    credits: VEO_31_FAST_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: GoogleSvg,
    options: {
      maxPromptLength: 3500,
      creditsPerSecond: VEO_31_FAST_CREDITS_PER_SECOND,
      duration: ['8s', '6s', '4s'],
      aspectRatio: ['16:9', '9:16'],
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
      audioDisabledCreditsMultiplier: VEO_31_FAST_AUDIO_OFF_MULTIPLIER,
      resolution: ['720p', '1080p', '4k'],
      resolutionCreditsMultiplier: VEO_31_FAST_RESOLUTION_MULTIPLIER,
      supportsReferenceImages: true,
      referenceImageConfig: {
        maxImages: 3,
        supportedTypes: ['character', 'object'],
        fieldMapping: { referenceImagesField: 'referenceImages' },
      },
    } as VideoModelOptions,
  },
  {
    id: 'gemini-omni-flash-text',
    title: 'Gemini Omni Flash',
    description:
      "Google's next-generation any-to-video model combining Gemini intelligence with generative media. Creates 720p videos up to 10 seconds with native audio from conversational prompts.",
    model: 'gemini-omni-flash-preview',
    isNew: false,
    isBest: true,
    credits: 60, // Fallback flat price (10s) when no duration is passed — 6 credits/s, Google's own 720p Omni rate (see GEMINI_OMNI_11_CREDITS_PER_SECOND)
    needsEndpointSuffix: false,
    icon: GoogleSvg,
    options: {
      // Output is a 720p clip with audio; generation parameters live in
      // response_format: aspect_ratio ('16:9' | '9:16') and duration
      // ('1s'–'10s', hard max 10 — the API FAILS above it rather than
      // clamping). No resolution control exists.
      creditsPerSecond: GEMINI_OMNI_11_CREDITS_PER_SECOND,
      duration: ['4', '5', '6', '7', '8', '9', '10'],
      aspectRatio: ['16:9', '9:16'],
      inputType: ['text'],
      generatesAudio: true,
      referenceSiblingId: 'gemini-omni-flash-reference',
    } as VideoModelOptions,
  },
  {
    id: 'gemini-omni-flash-reference',
    title: 'Gemini Omni Flash Reference',
    description:
      'Generate videos from reference images or edit an existing video with Gemini Omni Flash — consistent characters, objects, and conversational video editing in 10-second 720p clips with native audio.',
    model: 'gemini-omni-flash-preview',
    isNew: false,
    credits: 60, // Fixed 10s output (no duration control) — 6 credits/s, Google's own 720p Omni rate (see GEMINI_OMNI_11_CREDITS_PER_SECOND)
    needsEndpointSuffix: false,
    icon: GoogleSvg,
    options: {
      aspectRatio: [],
      inputType: ['text'],
      generatesAudio: true,
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      supportsReferenceVideo: true,
      // The odd one out: the clip is the footage being edited, not a motion
      // reference. The output mirrors its length (and is truncated at ~10s),
      // so it is billed per input second — see getOmniVideoEditCredits — and
      // goes to the interactions API inline as base64, hence the hard 20MB.
      referenceVideoConfig: {
        maxVideos: 1,
        minClipSeconds: 4,
        maxClipSeconds: 20,
        maxTotalSeconds: 20,
        maxBytesPerClip: 20 * 1024 * 1024,
        isEditSource: true,
      },
      referenceImageConfig: {
        maxImages: 5,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {},
      },
    } as VideoModelOptions,
  },
  {
    id: 'gemini-omni-flash-11-text',
    title: 'Gemini Omni Flash 1.1',
    description:
      "Google's newest any-to-video model: Gemini intelligence with Veo-style controls — 360p drafts up to 4K, 3-10 second clips, and native audio from conversational prompts.",
    model: GEMINI_OMNI_11_MODEL_ID,
    isNew: true,
    isBest: true,
    credits: GEMINI_OMNI_11_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: GoogleSvg,
    landingPage: '/ai-video-generator/gemini-omni-1-1-flash',
    options: {
      creditsPerSecond: GEMINI_OMNI_11_CREDITS_PER_SECOND,
      duration: GEMINI_OMNI_11_DURATIONS,
      defaultDuration: GEMINI_OMNI_11_DEFAULT_DURATION,
      aspectRatio: ['16:9', '9:16'],
      inputType: ['text'],
      generatesAudio: true,
      // The interactions API has no audio field at all — every render comes
      // back with a native stereo track (verified on all four flows). Without
      // this the UI offers an audio toggle that silently does nothing, and the
      // video orchestrator leaves the model's own dialogue and score playing
      // underneath a generated voiceover. No discount is lost: these entries
      // set no audioDisabledCreditsMultiplier, so "off" never cost less.
      alwaysGeneratesAudio: true,
      resolution: GEMINI_OMNI_11_RESOLUTIONS,
      defaultResolution: GEMINI_OMNI_11_DEFAULT_RESOLUTION,
      resolutionCreditsMultiplier: GEMINI_OMNI_11_RESOLUTION_MULTIPLIER,
      referenceSiblingId: 'gemini-omni-flash-11-reference',
    } as VideoModelOptions,
  },
  {
    id: 'gemini-omni-flash-11-reference',
    title: 'Gemini Omni Flash 1.1 Reference',
    description:
      'Generate from reference images or edit an existing clip conversationally with Gemini Omni 1.1 Flash — consistent characters and objects, native audio, up to 4K.',
    model: GEMINI_OMNI_11_MODEL_ID,
    isNew: true,
    credits: GEMINI_OMNI_11_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: GoogleSvg,
    landingPage: '/ai-video-generator/gemini-omni-1-1-flash',
    options: {
      // A reference render honours `duration` and falls back to 10s when it is
      // omitted (both probed live), so the ladder is offered and billed per
      // second. An EDIT ignores it — the output mirrors the source clip — and
      // is priced off the input instead (see getOmniVideoEditCredits); the
      // Omni branch in /api/generate-video withholds `duration` in that case.
      // Aspect follows the reference images / source clip, so no picker.
      creditsPerSecond: GEMINI_OMNI_11_CREDITS_PER_SECOND,
      duration: GEMINI_OMNI_11_DURATIONS,
      defaultDuration: GEMINI_OMNI_11_DEFAULT_DURATION,
      aspectRatio: [],
      inputType: ['text'],
      generatesAudio: true,
      // The interactions API has no audio field at all — every render comes
      // back with a native stereo track (verified on all four flows). Without
      // this the UI offers an audio toggle that silently does nothing, and the
      // video orchestrator leaves the model's own dialogue and score playing
      // underneath a generated voiceover. No discount is lost: these entries
      // set no audioDisabledCreditsMultiplier, so "off" never cost less.
      alwaysGeneratesAudio: true,
      resolution: GEMINI_OMNI_11_RESOLUTIONS,
      defaultResolution: GEMINI_OMNI_11_DEFAULT_RESOLUTION,
      resolutionCreditsMultiplier: GEMINI_OMNI_11_RESOLUTION_MULTIPLIER,
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      supportsReferenceVideo: true,
      // The clip is the footage being edited, not a motion reference, and it
      // goes to the interactions API inline as base64 — hence the 20MB cap.
      //
      // 10s, NOT the preview entry's 20: Google documents 10s as the ceiling
      // for an uploaded editing source, and 1.1 enforces it by FAILING the
      // interaction outright (probed 2026-08-28: a 4.0s and a 9.6s source both
      // rendered, a 12.1s source failed). The preview entry keeps 20 because
      // it silently truncates to ~10s instead of failing — a different
      // behaviour, and changing it is not this model's business. Edits do
      // preserve the source's length otherwise (4.01s in -> 4.01s out).
      referenceVideoConfig: {
        maxVideos: 1,
        minClipSeconds: 4,
        maxClipSeconds: 10,
        maxTotalSeconds: 10,
        maxBytesPerClip: 20 * 1024 * 1024,
        isEditSource: true,
      },
      referenceImageConfig: {
        maxImages: 5,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {},
      },
    } as VideoModelOptions,
  },
  {
    id: 'minimax-h3-max-text',
    title: 'MiniMax H3 Max',
    description:
      'The speed-tuned MiniMax H3 variant post-trained by fal — 480P/768P clips of 5-15 seconds with native synchronised audio, rendered in seconds rather than minutes.',
    model: 'minimax/h3-max/text-to-video',
    isNew: true,
    isFast: true,
    credits: MINIMAX_H3_MAX_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: HailuoSvg,
    options: {
      maxPromptLength: 4000,
      creditsPerSecond: MINIMAX_H3_MAX_CREDITS_PER_SECOND,
      duration: MINIMAX_H3_MAX_DURATIONS,
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: MINIMAX_H3_MAX_RESOLUTIONS,
      resolutionCreditsMultiplier: MINIMAX_H3_MAX_RESOLUTION_MULTIPLIER,
      inputType: ['text'],
      // Like standard H3, every render comes back with a native audio track
      // and the endpoint exposes no audio field at all, so `generate_audio`
      // cannot be honoured — no toggle, no audio-off discount.
      generatesAudio: true,
      alwaysGeneratesAudio: true,
      referenceSiblingId: 'minimax-h3-max-reference',
    } as VideoModelOptions,
  },
  {
    id: 'minimax-h3-max-reference',
    title: 'MiniMax H3 Max Reference',
    description:
      'Fast reference-to-video with MiniMax H3 Max — up to 9 reference images for consistent characters and style, 3 clips for motion, and 3 audio clips to set the voice.',
    model: 'minimax/h3-max/reference-to-video',
    isNew: true,
    isFast: true,
    credits: MINIMAX_H3_MAX_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: HailuoSvg,
    options: {
      maxPromptLength: 4000,
      creditsPerSecond: MINIMAX_H3_MAX_CREDITS_PER_SECOND,
      duration: MINIMAX_H3_MAX_DURATIONS,
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: MINIMAX_H3_MAX_RESOLUTIONS,
      // NO resolutionCreditsMultiplier, unlike the other two H3 Max entries:
      // this endpoint's output is a flat $0.08 per second at BOTH tiers (fal
      // prices output by duration alone here, and splits 480p/768p only in
      // the reference-token table). Generating at 480p still costs much less
      // overall — reference clips are ~2.6x cheaper in tokens there.
      inputType: ['text'],
      generatesAudio: true,
      alwaysGeneratesAudio: true,
      referenceTokenPricing: MINIMAX_H3_MAX_REFERENCE_TOKENS,
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      referenceImageConfig: {
        maxImages: 9,
        supportedTypes: ['character', 'object', 'style'],
        // fal takes a flat array of URLs — no per-type split, unlike Kling's
        // `elements`. Each reference is cited from the prompt by its position
        // ("Image 1", "Video 2", "Audio 1").
        fieldMapping: {
          imageUrlsField: 'reference_image_urls',
        },
      },
      // Motion and camera guidance from up to 3 clips, billed per output
      // second only — see MINIMAX_H3_MAX_REFERENCE_VIDEO.
      supportsReferenceVideo: true,
      referenceVideoConfig: MINIMAX_H3_MAX_REFERENCE_VIDEO,
      // Same rule as standard H3: audio can never be the only reference, so
      // at least one reference image or video must accompany it.
      supportsReferenceAudio: true,
      referenceAudioConfig: {
        maxAudios: 3,
        maxTotalSeconds: 15,
        requiresImageReference: true,
        minClipSeconds: 2,
        maxClipSeconds: 15,
      },
    } as VideoModelOptions,
  },
  {
    id: 'grok-imagine-text',
    title: 'Grok Imagine',
    description:
      'xAI Grok Imagine video model with high-quality text-to-video generation supporting 1-15 second videos',
    model: 'grok-imagine-video',
    isNew: false,
    isBest: true,
    credits: 10, // Default display (5s × 2 @ 480p)
    needsEndpointSuffix: false,
    icon: XAISvg,
    options: {
      creditsPerSecond: 2, // 480p baseline; 720p via resolutionCreditsMultiplier
      duration: ['5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
      aspectRatio: ['16:9', '4:3', '1:1', '9:16', '3:4', '3:2', '2:3'],
      inputType: ['text'],
      resolution: ['480p', '720p'],
      resolutionCreditsMultiplier: { '720p': 1.5 }, // 480p 2/s, 720p 3/s
      referenceSiblingId: 'grok-imagine-reference',
    } as VideoModelOptions,
  },
  {
    id: 'grok-imagine-reference',
    title: 'Grok Imagine Reference',
    description:
      'Generate videos with consistent characters and objects using up to 7 reference images. Ideal for virtual try-on, product placement, and character-consistent storytelling.',
    model: 'grok-imagine-video',
    isNew: false,
    isBest: true,
    credits: 10, // Default display (5s × 2 @ 480p)
    needsEndpointSuffix: false,
    icon: XAISvg,
    options: {
      creditsPerSecond: 2, // 480p baseline; 720p via resolutionCreditsMultiplier
      duration: ['5', '6', '7', '8', '9', '10'],
      aspectRatio: ['16:9', '4:3', '1:1', '9:16', '3:4', '3:2', '2:3'],
      inputType: ['text'],
      resolution: ['480p', '720p'],
      resolutionCreditsMultiplier: { '720p': 1.5 }, // 480p 2/s, 720p 3/s
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      referenceImageConfig: {
        maxImages: 7,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {},
      },
    } as VideoModelOptions,
  },
  {
    id: 'grok-imagine-text-1.5',
    title: 'Grok Imagine 1.5 Text',
    description:
      'xAI Grok Imagine Video 1.5 text-to-video with synchronized native audio and up to 1080p output.',
    model: 'grok-imagine-video-1.5',
    isNew: true,
    isBest: true,
    credits: 20, // Default display (5s × 4 @ 480p)
    needsEndpointSuffix: false,
    icon: XAISvg,
    options: {
      creditsPerSecond: 4, // 480p baseline ($0.08/s ÷ $0.02/credit); higher res via resolutionCreditsMultiplier
      duration: ['5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
      aspectRatio: ['16:9', '4:3', '1:1', '9:16', '3:4', '3:2', '2:3'],
      inputType: ['text'],
      resolution: ['480p', '720p', '1080p'],
      resolutionCreditsMultiplier: { '720p': 1.75, '1080p': 3.125 }, // 480p 4/s ($0.08), 720p 7/s ($0.14), 1080p 12.5/s ($0.25)
      generatesAudio: true,
      referenceSiblingId: 'grok-imagine-reference-1.5',
    } as VideoModelOptions,
  },
  {
    id: 'grok-imagine-reference-1.5',
    title: 'Grok Imagine 1.5 Reference',
    description:
      'Grok Imagine Video 1.5 reference-to-video — up to 7 reference images keep every character, product, and style consistent across the clip (720p max).',
    model: 'grok-imagine-video-1.5',
    isNew: true,
    isBest: true,
    credits: 20, // Default display (5s × 4 @ 480p)
    needsEndpointSuffix: false,
    icon: XAISvg,
    options: {
      creditsPerSecond: 4, // 480p baseline; 720p via resolutionCreditsMultiplier
      duration: ['5', '6', '7', '8', '9', '10'],
      aspectRatio: ['16:9', '4:3', '1:1', '9:16', '3:4', '3:2', '2:3'],
      inputType: ['text'],
      // Reference mode tops out at 720p (t2v/i2v go to 1080p).
      resolution: ['480p', '720p'],
      resolutionCreditsMultiplier: { '720p': 1.75 }, // 480p 4/s, 720p 7/s
      generatesAudio: true,
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      referenceImageConfig: {
        maxImages: 7,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {},
      },
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-fast-text',
    title: 'Seedance 2.0 Fast',
    description:
      'Fast version of Seedance 2.0 with cinematic output, native audio, and real-world physics at reduced generation times.',
    model: 'bytedance/seedance-2.0/fast/text-to-video',
    isNew: false,
    isEnterprise: false,
    credits: 30, // Default display (5s × 6 @ 720p)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2',
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 6, // 6 credits/s @ 720p (480p ×0.5 -> 3/s); WaveSpeed 480p ≈ $0.09/s
      duration: [
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      // Seedance 2.0 Fast supports the full resolution ladder incl. 4K
      // (480p ×0.5, 1080p ×2.5, 4K ×5 — same cost curve as the 2.0 family).
      resolution: ['480p', '720p', '1080p', '4k'],
      resolutionCreditsMultiplier: { '480p': 0.5, '1080p': 2.5, '4k': 5 },
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsReferenceAudio: true,
      // Live-probed 2026-08-03: Seedance rejects audio-only input
      // ("reference_audios requires at least one image ... or reference
      // video"), so an image reference must accompany the audio.
      referenceAudioConfig: {
        maxAudios: 3,
        maxTotalSeconds: 15,
        requiresImageReference: true,
      },
    } as VideoModelOptions,
  },
  {
    id: 'minimax-h3-text',
    title: 'MiniMax H3',
    description:
      "MiniMax's latest model generating coherent, cinematic videos with native audio from text prompts — 768P or 2K, 4-15 seconds.",
    model: 'minimax/h3/text-to-video',
    isNew: true,
    isBest: true,
    credits: MINIMAX_H3_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: HailuoSvg,
    landingPage: '/ai-video-generator/minimax-h3',
    options: {
      maxPromptLength: 4000,
      creditsPerSecond: MINIMAX_H3_CREDITS_PER_SECOND,
      duration: MINIMAX_H3_DURATIONS,
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: MINIMAX_H3_RESOLUTIONS,
      resolutionCreditsMultiplier: MINIMAX_H3_RESOLUTION_MULTIPLIER,
      inputType: ['text'],
      // H3 always renders native stereo audio from the prompt — the v2 API
      // has no audio field at all, so generateAudio:false cannot be honored.
      generatesAudio: true,
      alwaysGeneratesAudio: true,
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-5-text',
    title: 'Seedance 2.5',
    description:
      "ByteDance's flagship video model — one coherent take up to 30 seconds, cinematic camera work, real-world physics, and native audio in 11 languages.",
    model: 'bytedance/seedance-2.5/text-to-video',
    isNew: true,
    isBest: true,
    isEnterprise: false,
    credits: SEEDANCE_25_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2-5',
    options: {
      maxPromptLength: SEEDANCE_25_MAX_PROMPT_LENGTH,
      creditsPerSecond: SEEDANCE_25_CREDITS_PER_SECOND,
      duration: SEEDANCE_25_DURATIONS,
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: SEEDANCE_25_RESOLUTIONS,
      resolutionCreditsMultiplier: SEEDANCE_25_RESOLUTION_MULTIPLIER,
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-5-turbo-text',
    title: 'Seedance 2.5 Turbo',
    description:
      'The fast, lower-cost tier of Seedance 2.5 — the same 30-second takes and native audio at roughly half the price, with 1080p output.',
    model: 'bytedance/seedance-2.5/text-to-video-turbo',
    isNew: true,
    isBest: true,
    isEnterprise: false,
    credits: SEEDANCE_25_TURBO_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2-5',
    options: {
      maxPromptLength: SEEDANCE_25_MAX_PROMPT_LENGTH,
      creditsPerSecond: SEEDANCE_25_TURBO_CREDITS_PER_SECOND,
      duration: SEEDANCE_25_DURATIONS,
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: SEEDANCE_25_TURBO_RESOLUTIONS,
      resolutionCreditsMultiplier: SEEDANCE_25_TURBO_RESOLUTION_MULTIPLIER,
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-5-turbo-reference',
    title: 'Seedance 2.5 Turbo Reference',
    description:
      'Fast reference-to-video with Seedance 2.5 — up to 30 reference images plus reference audio, at the turbo tier’s lower price.',
    model: 'bytedance/seedance-2.5/reference-to-video-turbo',
    isNew: true,
    isEnterprise: false,
    credits: SEEDANCE_25_TURBO_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2-5',
    options: {
      maxPromptLength: SEEDANCE_25_MAX_PROMPT_LENGTH,
      creditsPerSecond: SEEDANCE_25_TURBO_CREDITS_PER_SECOND,
      duration: SEEDANCE_25_DURATIONS,
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: SEEDANCE_25_TURBO_RESOLUTIONS,
      resolutionCreditsMultiplier: SEEDANCE_25_TURBO_RESOLUTION_MULTIPLIER,
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      referenceImageConfig: {
        maxImages: 30,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {
          imageUrlsField: 'reference_images',
        },
      },
      // Motion, camera and pacing guidance from reference clips, passed
      // by URL alongside the image references. Also satisfies the
      // companion requirement for reference audio, which the provider's
      // own rejection spells out: "reference_audios requires at least one
      // image ... or reference video".
      supportsReferenceVideo: true,
      referenceVideoConfig: SEEDANCE_25_REFERENCE_VIDEO,
      supportsReferenceAudio: true,
      referenceAudioConfig: {
        maxAudios: 10,
        maxTotalSeconds: 30,
        requiresImageReference: true,
        minClipSeconds: 2,
        maxClipSeconds: 30,
        acceptedFormats: ['wav', 'mp3'],
        maxBytesPerClip: 15 * 1024 * 1024,
      },
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-5-reference',
    title: 'Seedance 2.5 Reference',
    description:
      'Guide a Seedance 2.5 take with up to 30 reference images plus reference audio — consistent characters, products, storyboards, and keyframes across a single 30-second shot.',
    model: 'bytedance/seedance-2.5/reference-to-video',
    isNew: true,
    isBest: true,
    isEnterprise: false,
    credits: SEEDANCE_25_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2-5',
    options: {
      maxPromptLength: SEEDANCE_25_MAX_PROMPT_LENGTH,
      creditsPerSecond: SEEDANCE_25_CREDITS_PER_SECOND,
      duration: SEEDANCE_25_DURATIONS,
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: SEEDANCE_25_RESOLUTIONS,
      resolutionCreditsMultiplier: SEEDANCE_25_RESOLUTION_MULTIPLIER,
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      referenceImageConfig: {
        maxImages: 30,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {
          imageUrlsField: 'reference_images',
        },
      },
      // Motion, camera and pacing guidance from reference clips, passed
      // by URL alongside the image references. Also satisfies the
      // companion requirement for reference audio, which the provider's
      // own rejection spells out: "reference_audios requires at least one
      // image ... or reference video".
      supportsReferenceVideo: true,
      referenceVideoConfig: SEEDANCE_25_REFERENCE_VIDEO,
      supportsReferenceAudio: true,
      // Seedance 2.5 is the one model in the family that accepts audio-only
      // input, but the Video Generator always pairs audio with an image
      // reference, so the stricter rule is kept here too. Per-clip limits come
      // from the ModelArk multimodal-input table (WAV/MP3, 2-30s, <=15MB).
      referenceAudioConfig: {
        maxAudios: 10,
        maxTotalSeconds: 30,
        requiresImageReference: true,
        minClipSeconds: 2,
        maxClipSeconds: 30,
        acceptedFormats: ['wav', 'mp3'],
        maxBytesPerClip: 15 * 1024 * 1024,
      },
    } as VideoModelOptions,
  },
  {
    id: 'flux-3-text',
    title: 'FLUX 3',
    description:
      "Black Forest Labs' video model — cinematic 5-20 second takes from a text prompt, with coherent motion, composed camera work, and synchronized audio at up to 1080p.",
    model: 'black-forest-labs/flux-3/text-to-video',
    isNew: true,
    isBest: true,
    isEnterprise: false,
    credits: FLUX_3_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: FluxSvg,
    options: {
      creditsPerSecond: FLUX_3_CREDITS_PER_SECOND,
      duration: FLUX_3_DURATIONS,
      aspectRatio: FLUX_3_ASPECT_RATIOS,
      resolution: FLUX_3_RESOLUTIONS,
      resolutionCreditsMultiplier: FLUX_3_RESOLUTION_MULTIPLIER,
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
    } as VideoModelOptions,
  },
  {
    id: 'flux-3-max-text',
    title: 'FLUX 3 Max',
    description:
      'The sharpest FLUX 3 — Black Forest Labs tuned it for closer prompt adherence and better visual quality at lower latency. 5-20 second takes with synchronized audio at up to 1080p.',
    model: 'blackforestlabs/flux-3-max/text-to-video',
    isNew: true,
    isBest: true,
    isEnterprise: false,
    get credits() {
      return flux3MaxPricing().credits;
    },
    get isPromo() {
      return flux3MaxPricing().promo;
    },
    needsEndpointSuffix: false,
    icon: FluxSvg,
    options: {
      get creditsPerSecond() {
        return flux3MaxPricing().creditsPerSecond;
      },
      get resolutionCreditsMultiplier() {
        return flux3MaxPricing().resolutionCreditsMultiplier;
      },
      duration: FLUX_3_DURATIONS,
      aspectRatio: FLUX_3_ASPECT_RATIOS,
      resolution: FLUX_3_RESOLUTIONS,
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
      // "Add media" switches to the keyframes entry, which pins the images
      // to frames — FLUX 3 Max has no reference-to-video endpoint.
      referenceSiblingId: 'flux-3-max-keyframes',
    } as VideoModelOptions,
  },
  {
    id: 'flux-3-max-keyframes',
    title: 'FLUX 3 Max Keyframes',
    description:
      'Pin up to 10 images as keyframes and FLUX 3 Max animates between them — the first image opens the clip, the last one closes it, and the rest are spaced evenly in between, in the order you add them.',
    model: 'blackforestlabs/flux-3-max/keyframes-to-video',
    isNew: true,
    isBest: true,
    isEnterprise: false,
    get credits() {
      return flux3MaxPricing().credits;
    },
    get isPromo() {
      return flux3MaxPricing().promo;
    },
    needsEndpointSuffix: false,
    icon: FluxSvg,
    options: {
      get creditsPerSecond() {
        return flux3MaxPricing().creditsPerSecond;
      },
      get resolutionCreditsMultiplier() {
        return flux3MaxPricing().resolutionCreditsMultiplier;
      },
      duration: FLUX_3_DURATIONS,
      aspectRatio: FLUX_3_ASPECT_RATIOS,
      resolution: FLUX_3_RESOLUTIONS,
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      // Every type is accepted so all library tabs stay open; the type is
      // ignored — each image becomes a keyframe (see generate-video).
      referenceImageConfig: {
        maxImages: FLUX_3_MAX_MAX_KEYFRAMES,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {},
      },
    } as VideoModelOptions,
  },
  {
    id: 'wan-3-text',
    title: 'WAN 3.0',
    description:
      "Alibaba's flagship video model — a single continuous take of up to 30 seconds at native 1080p, with scored audio generated in the same pass and cinematic camera work.",
    model: 'alibaba/wan-3.0/text-to-video',
    isNew: true,
    isBest: true,
    isEnterprise: false,
    credits: WAN_3_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: WanSvg,
    landingPage: '/ai-video-generator/wan-3-0',
    options: {
      maxPromptLength: WAN_3_MAX_PROMPT_LENGTH,
      creditsPerSecond: WAN_3_CREDITS_PER_SECOND,
      duration: WAN_3_DURATIONS,
      aspectRatio: WAN_3_ASPECT_RATIOS,
      resolution: WAN_3_RESOLUTIONS,
      resolutionCreditsMultiplier: WAN_3_RESOLUTION_MULTIPLIER,
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
    } as VideoModelOptions,
  },
  {
    id: 'wan-3-reference',
    title: 'WAN 3.0 Reference',
    description:
      'Guide a WAN 3.0 take with reference images and reference audio — consistent characters, products and motion held across a single shot of up to 30 seconds.',
    model: 'alibaba/wan-3.0/reference-to-video',
    isNew: true,
    isBest: true,
    isEnterprise: false,
    credits: WAN_3_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: WanSvg,
    landingPage: '/ai-video-generator/wan-3-0',
    options: {
      maxPromptLength: WAN_3_MAX_PROMPT_LENGTH,
      creditsPerSecond: WAN_3_CREDITS_PER_SECOND,
      duration: WAN_3_DURATIONS,
      aspectRatio: WAN_3_ASPECT_RATIOS,
      resolution: WAN_3_RESOLUTIONS,
      resolutionCreditsMultiplier: WAN_3_RESOLUTION_MULTIPLIER,
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      // WAN 3.0 documents up to 20 multimodal references, but the shared
      // WaveSpeed submit path caps `reference_images` at 9 and
      // `reference_audios` at 3 — the config mirrors what we actually
      // transmit so the picker never accepts inputs that get dropped.
      referenceImageConfig: {
        maxImages: 9,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {
          imageUrlsField: 'reference_images',
        },
      },
      supportsReferenceAudio: true,
      referenceAudioConfig: {
        maxAudios: 3,
        maxTotalSeconds: 30,
        requiresImageReference: true,
        minClipSeconds: 2,
        maxClipSeconds: 30,
        acceptedFormats: ['wav', 'mp3'],
        maxBytesPerClip: 15 * 1024 * 1024,
      },
    } as VideoModelOptions,
  },
  {
    id: 'kling-40-text',
    title: 'Kling 4.0',
    description:
      "Kuaishou's newest Kling model — cinematic takes of 3 to 30 seconds at 720p or 1080p with synchronized stereo audio, including 21:9 ultrawide.",
    model: 'kwaivgi/kling-v4.0/text-to-video',
    isNew: true,
    isBest: true,
    isEnterprise: false,
    credits: KLING_40_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: KlingSvg,
    landingPage: '/ai-video-generator/kling-4-0',
    options: {
      maxPromptLength: KLING_40_MAX_PROMPT_LENGTH,
      creditsPerSecond: KLING_40_CREDITS_PER_SECOND,
      duration: KLING_40_DURATIONS,
      defaultDuration: KLING_40_DEFAULT_DURATION,
      aspectRatio: KLING_40_ASPECT_RATIOS,
      resolution: KLING_40_RESOLUTIONS,
      resolutionCreditsMultiplier: KLING_40_RESOLUTION_MULTIPLIER,
      inputType: ['text'],
      // No audio field on any Kling 4.0 endpoint: every render has sound.
      generatesAudio: true,
      alwaysGeneratesAudio: true,
    } as VideoModelOptions,
  },
  {
    id: 'kling-40-reference',
    title: 'Kling 4.0 Reference',
    description:
      'Build a Kling 4.0 take from up to 10 reference images and 5 reference clips for motion and camera. Cite them in the prompt as @Image1 and @Video1.',
    model: 'kwaivgi/kling-v4.0/reference-to-video',
    isNew: true,
    isBest: true,
    isEnterprise: false,
    credits: KLING_40_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: KlingSvg,
    landingPage: '/ai-video-generator/kling-4-0',
    options: {
      maxPromptLength: KLING_40_MAX_PROMPT_LENGTH,
      creditsPerSecond: KLING_40_CREDITS_PER_SECOND,
      duration: KLING_40_DURATIONS,
      defaultDuration: KLING_40_DEFAULT_DURATION,
      // The endpoint also takes `auto` (follow the references); a concrete
      // ratio is always sent, like WAN 3.0 Reference.
      aspectRatio: KLING_40_ASPECT_RATIOS,
      resolution: KLING_40_RESOLUTIONS,
      resolutionCreditsMultiplier: KLING_40_RESOLUTION_MULTIPLIER,
      inputType: ['text'],
      generatesAudio: true,
      alwaysGeneratesAudio: true,
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      // A flat `images` array cited by position (@Image1...). The builder
      // adds the tags to the prompt when the user wrote none, so every file
      // is bound to the request.
      referenceImageConfig: {
        maxImages: KLING_40_MAX_IMAGES,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {
          imageUrlsField: 'images',
        },
      },
      // Attaching a clip re-prices the whole job as counted seconds — see
      // KLING_40_REFERENCE_VIDEO.
      supportsReferenceVideo: true,
      referenceVideoConfig: KLING_40_REFERENCE_VIDEO,
      // No voice clips: the endpoint lists `audios` (up to 3, 5-30s), but
      // WaveSpeed refused every live request carrying one with code 1405,
      // MP3 or WAV, with or without a clip (Oct 2026). Offer them again only
      // after a request with audio has completed.
    } as VideoModelOptions,
  },
  {
    id: 'kling-40-keyframes',
    title: 'Kling 4.0 Keyframes',
    description:
      'Direct a Kling 4.0 take with up to 10 frames — the first image opens the clip, the last one closes it, and the rest play out in between in the order you add them.',
    // Not a WaveSpeed path of its own: the images go to the image-to-video
    // endpoint as `image`, `keyframes` and `last_image` (see the WaveSpeed
    // branch of /api/generate-video). A distinct path keeps the
    // text/image/reference sibling lookups from ever landing here.
    model: KLING_40_KEYFRAMES_MODEL,
    isNew: true,
    isBest: true,
    isEnterprise: false,
    credits: KLING_40_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: KlingSvg,
    landingPage: '/ai-video-generator/kling-4-0',
    options: {
      maxPromptLength: KLING_40_MAX_PROMPT_LENGTH,
      creditsPerSecond: KLING_40_CREDITS_PER_SECOND,
      duration: KLING_40_DURATIONS,
      defaultDuration: KLING_40_DEFAULT_DURATION,
      // Framing follows the first image; a single entry hides the selector.
      aspectRatio: ['16:9'],
      resolution: KLING_40_RESOLUTIONS,
      resolutionCreditsMultiplier: KLING_40_RESOLUTION_MULTIPLIER,
      inputType: ['text'],
      generatesAudio: true,
      alwaysGeneratesAudio: true,
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      // Every type is accepted so all library tabs stay open; the type is
      // ignored — each image becomes a frame, in the order added.
      referenceImageConfig: {
        maxImages: KLING_40_MAX_IMAGES,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {},
      },
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-turbo-text',
    title: 'Seedance 2.0 Turbo',
    description:
      'High-resolution 720p/1080p output at near-480p speed, with native audio, real-world physics, and director-level camera control.',
    model: 'bytedance/seedance-2.0/text-to-video-turbo',
    isNew: false,
    isBest: true,
    isEnterprise: false,
    credits: 35, // Default display (5s × 7 @ 720p)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2',
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 7, // WaveSpeed 720p: $0.14/s ÷ $0.02/credit
      duration: [
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: ['720p', '1080p'],
      resolutionCreditsMultiplier: { '1080p': 1.07 }, // 1080p costs ~$0.15/s vs $0.14/s
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsReferenceAudio: true,
      // Live-probed 2026-08-03: Seedance rejects audio-only input
      // ("reference_audios requires at least one image ... or reference
      // video"), so an image reference must accompany the audio.
      referenceAudioConfig: {
        maxAudios: 3,
        maxTotalSeconds: 15,
        requiresImageReference: true,
      },
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-text',
    title: 'Seedance 2.0',
    description:
      'Cinematic output with native audio, real-world physics, and director-level camera control. Up to 1080p resolution.',
    model: 'bytedance/seedance-2.0/text-to-video',
    isNew: false,
    isBest: true,
    isEnterprise: false,
    credits: 60, // Default display (5s × 12 @ 720p)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2',
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 12, // WaveSpeed 720p: $0.24/s ÷ $0.02/credit
      duration: [
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: ['480p', '720p', '1080p', '4k'],
      resolutionCreditsMultiplier: { '480p': 0.5, '1080p': 2.5, '4k': 5 },
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsReferenceAudio: true,
      // Live-probed 2026-08-03: Seedance rejects audio-only input
      // ("reference_audios requires at least one image ... or reference
      // video"), so an image reference must accompany the audio.
      referenceAudioConfig: {
        maxAudios: 3,
        maxTotalSeconds: 15,
        requiresImageReference: true,
      },
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-mini-text',
    // Free-plan users may use the Mini family (480p, <=5s) — enforced in
    // /api/generate-video; the welcome gift makes the first video affordable.
    freeUserAccess: true,
    freePlanPolicy: {
      resolutions: ['480p'] as ResolutionType[],
      maxDurationSeconds: 5,
    },
    title: 'Seedance 2.0 Mini',
    description:
      "ByteDance's fastest, lowest-cost Seedance 2.0 model — cinematic multi-shot video with native audio and AI camera control.",
    model: 'bytedance/seedance-2.0-mini/text-to-video',
    isNew: false,
    isEnterprise: false,
    credits: 20, // Default display (5s × 4 @ 720p)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2',
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 4, // 480p = 2 credits/s (×0.5 multiplier); subsidized promo rate below WaveSpeed cost
      duration: [
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: ['480p', '720p', '1080p', '4k'],
      resolutionCreditsMultiplier: { '480p': 0.5, '1080p': 2.5, '4k': 5 },
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsReferenceAudio: true,
      // Live-probed 2026-08-03: Seedance rejects audio-only input
      // ("reference_audios requires at least one image ... or reference
      // video"), so an image reference must accompany the audio.
      referenceAudioConfig: {
        maxAudios: 3,
        maxTotalSeconds: 15,
        requiresImageReference: true,
      },
    } as VideoModelOptions,
  },

  {
    id: 'seedance-2-reference',
    title: 'Seedance 2.0 Reference',
    description:
      'Generate videos with consistent characters and objects using up to 9 reference images, with native audio.',
    model: 'bytedance/seedance-2.0/reference-to-video',
    isNew: false,
    isBest: true,
    isEnterprise: false,
    credits: 60, // Default display (5s × 12 @ 720p)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2',
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 12, // WaveSpeed 720p: $0.24/s ÷ $0.02/credit
      duration: [
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: ['480p', '720p', '1080p', '4k'],
      resolutionCreditsMultiplier: { '480p': 0.5, '1080p': 2.5, '4k': 5 },
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      referenceImageConfig: {
        maxImages: 9,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {
          imageUrlsField: 'image_urls',
        },
      },
      // Motion, camera and pacing guidance from reference clips, passed
      // by URL alongside the image references. Also satisfies the
      // companion requirement for reference audio, which the provider's
      // own rejection spells out: "reference_audios requires at least one
      // image ... or reference video".
      supportsReferenceVideo: true,
      referenceVideoConfig: SEEDANCE_20_REFERENCE_VIDEO,
      supportsReferenceAudio: true,
      // Live-probed 2026-08-03: Seedance rejects audio-only input
      // ("reference_audios requires at least one image ... or reference
      // video"), so an image reference must accompany the audio.
      referenceAudioConfig: {
        maxAudios: 3,
        maxTotalSeconds: 15,
        requiresImageReference: true,
      },
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-fast-reference',
    title: 'Seedance 2.0 Fast Reference',
    description:
      'Fast reference-to-video with consistent characters and objects using up to 9 reference images.',
    model: 'bytedance/seedance-2.0/fast/reference-to-video',
    isNew: false,
    isEnterprise: false,
    credits: 30, // Default display (5s × 6 @ 720p)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2',
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 6, // 6 credits/s @ 720p (480p ×0.5 -> 3/s); WaveSpeed 480p ≈ $0.09/s
      duration: [
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      // Seedance 2.0 Fast supports the full resolution ladder incl. 4K
      // (480p ×0.5, 1080p ×2.5, 4K ×5 — same cost curve as the 2.0 family).
      resolution: ['480p', '720p', '1080p', '4k'],
      resolutionCreditsMultiplier: { '480p': 0.5, '1080p': 2.5, '4k': 5 },
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      referenceImageConfig: {
        maxImages: 9,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {
          imageUrlsField: 'image_urls',
        },
      },
      // Motion, camera and pacing guidance from reference clips, passed
      // by URL alongside the image references. Also satisfies the
      // companion requirement for reference audio, which the provider's
      // own rejection spells out: "reference_audios requires at least one
      // image ... or reference video".
      supportsReferenceVideo: true,
      referenceVideoConfig: SEEDANCE_20_REFERENCE_VIDEO,
      supportsReferenceAudio: true,
      // Live-probed 2026-08-03: Seedance rejects audio-only input
      // ("reference_audios requires at least one image ... or reference
      // video"), so an image reference must accompany the audio.
      referenceAudioConfig: {
        maxAudios: 3,
        maxTotalSeconds: 15,
        requiresImageReference: true,
      },
    } as VideoModelOptions,
  },
  {
    id: 'minimax-h3-reference',
    title: 'MiniMax H3 Reference',
    description:
      'Generate coherent 768P/2K videos guided by up to 9 reference images for consistent characters, products, and style — plus reference audio to set the voice.',
    model: 'minimax/h3/reference-to-video',
    isNew: true,
    isBest: true,
    credits: MINIMAX_H3_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: HailuoSvg,
    landingPage: '/ai-video-generator/minimax-h3',
    options: {
      maxPromptLength: 4000,
      creditsPerSecond: MINIMAX_H3_CREDITS_PER_SECOND,
      duration: MINIMAX_H3_DURATIONS,
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: MINIMAX_H3_RESOLUTIONS,
      resolutionCreditsMultiplier: MINIMAX_H3_RESOLUTION_MULTIPLIER,
      inputType: ['text'],
      generatesAudio: true,
      alwaysGeneratesAudio: true,
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      referenceImageConfig: {
        maxImages: 9,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {
          imageUrlsField: 'reference_images',
        },
      },
      // Motion/camera guidance from up to 3 clips, alongside the image and
      // audio references. Never combinable with a start/end frame — H3's
      // reference roles and first_frame/last_frame are mutually exclusive.
      supportsReferenceVideo: true,
      referenceVideoConfig: MINIMAX_H3_REFERENCE_VIDEO,
      // H3's schema: "Audio cannot be provided alone" — at least one image
      // OR reference video must accompany it. Its audio limits are stricter
      // than the picker's: WAV/MP3 only, 2-15s per clip, <=15MB each.
      supportsReferenceAudio: true,
      referenceAudioConfig: {
        maxAudios: 3,
        maxTotalSeconds: 15,
        requiresImageReference: true,
        minClipSeconds: 2,
        maxClipSeconds: 15,
        acceptedFormats: ['wav', 'mp3'],
        maxBytesPerClip: 15 * 1024 * 1024,
      },
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-mini-reference',
    // Free-plan users may use the Mini family (480p, <=5s) — enforced in
    // /api/generate-video; the welcome gift makes the first video affordable.
    freeUserAccess: true,
    freePlanPolicy: {
      resolutions: ['480p'] as ResolutionType[],
      maxDurationSeconds: 5,
    },
    title: 'Seedance 2.0 Mini Reference',
    description:
      'Fast, low-cost reference-to-video with consistent characters and objects using up to 9 reference images, with native audio.',
    model: 'bytedance/seedance-2.0-mini/reference-to-video',
    isNew: false,
    isEnterprise: false,
    credits: 20, // Default display (5s × 4 @ 720p)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2',
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 4, // 480p = 2 credits/s (×0.5 multiplier); subsidized promo rate below WaveSpeed cost
      duration: [
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: ['480p', '720p', '1080p', '4k'],
      resolutionCreditsMultiplier: { '480p': 0.5, '1080p': 2.5, '4k': 5 },
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      referenceImageConfig: {
        maxImages: 9,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {
          imageUrlsField: 'image_urls',
        },
      },
      // Motion, camera and pacing guidance from reference clips, passed
      // by URL alongside the image references. Also satisfies the
      // companion requirement for reference audio, which the provider's
      // own rejection spells out: "reference_audios requires at least one
      // image ... or reference video".
      supportsReferenceVideo: true,
      referenceVideoConfig: SEEDANCE_20_REFERENCE_VIDEO,
      supportsReferenceAudio: true,
      // Live-probed 2026-08-03: Seedance rejects audio-only input
      // ("reference_audios requires at least one image ... or reference
      // video"), so an image reference must accompany the audio.
      referenceAudioConfig: {
        maxAudios: 3,
        maxTotalSeconds: 15,
        requiresImageReference: true,
      },
    } as VideoModelOptions,
  },
  {
    id: 'kling-26-pro-text',
    title: 'Kling 2.6 Pro',
    description:
      'Advanced text-to-video generation with exceptional quality, emotional orchestral expressions, and cinematic output',
    model: 'fal-ai/kling-video/v2.6/pro/text-to-video',
    isNew: false,
    isBest: true,
    credits: 30,
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      duration: ['5', '10'],
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['text'],
      generatesAudio: true,
    } as VideoModelOptions,
  },
  {
    id: 'veo31-lite-text',
    // Veo runs on Vertex AI (covered by GCP credits). Free users pay the
    // standard credit prices, capped at 720p. Enforced in /api/generate-video.
    freeUserAccess: true,
    freePlanPolicy: { resolutions: ['720p'] as ResolutionType[] },
    title: 'Veo 3.1 Lite',
    description:
      'Most cost-effective Google Veo 3.1 model with text-to-video generation and built-in audio',
    model: 'veo-3.1-lite-generate-preview',
    isNew: false,
    isBest: true,
    credits: VEO_31_LITE_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: GoogleSvg,
    options: {
      maxPromptLength: 3500,
      creditsPerSecond: VEO_31_LITE_CREDITS_PER_SECOND,
      duration: ['8s', '6s', '4s'],
      aspectRatio: ['16:9', '9:16'],
      inputType: ['text'],
      generatesAudio: true,
      resolution: ['720p', '1080p'],
      resolutionCreditsMultiplier: VEO_31_LITE_RESOLUTION_MULTIPLIER,
    } as VideoModelOptions,
  },
  {
    id: 'veo31-lite-image',
    // Veo runs on Vertex AI (covered by GCP credits). Free users pay the
    // standard credit prices, capped at 720p. Enforced in /api/generate-video.
    freeUserAccess: true,
    freePlanPolicy: { resolutions: ['720p'] as ResolutionType[] },
    title: 'Veo 3.1 Lite Image',
    description:
      'Most cost-effective Google Veo 3.1 image-to-video model with built-in audio',
    model: 'veo-3.1-lite-generate-preview',
    isNew: false,
    isBest: true,
    credits: VEO_31_LITE_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: GoogleSvg,
    options: {
      maxPromptLength: 3500,
      creditsPerSecond: VEO_31_LITE_CREDITS_PER_SECOND,
      duration: ['8s'],
      aspectRatio: ['16:9', '9:16'],
      inputType: ['image'],
      generatesAudio: true,
      resolution: ['720p', '1080p'],
      resolutionCreditsMultiplier: VEO_31_LITE_RESOLUTION_MULTIPLIER,
    } as VideoModelOptions,
  },
  {
    id: 'veo31-fast-image',
    // Veo runs on Vertex AI (covered by GCP credits). Free users pay the
    // standard credit prices, capped at 720p. Enforced in /api/generate-video.
    freeUserAccess: true,
    freePlanPolicy: { resolutions: ['720p'] as ResolutionType[] },
    title: 'Veo 3.1 Fast Image',
    description:
      'Fast image-to-video transformation with Google Veo 3.1 in up to 4K resolution',
    model: 'veo-3.1-fast-generate-001',
    isNew: false,
    isBest: true,
    credits: VEO_31_FAST_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: GoogleSvg,
    options: {
      maxPromptLength: 3500,
      creditsPerSecond: VEO_31_FAST_CREDITS_PER_SECOND,
      duration: ['8s'],
      aspectRatio: ['16:9', '9:16'],
      inputType: ['image'],
      generatesAudio: true,
      supportsAudioToggle: true,
      audioDisabledCreditsMultiplier: VEO_31_FAST_AUDIO_OFF_MULTIPLIER,
      resolution: ['720p', '1080p', '4k'],
      resolutionCreditsMultiplier: VEO_31_FAST_RESOLUTION_MULTIPLIER,
      supportsReferenceImages: true,
      referenceImageConfig: {
        maxImages: 3,
        supportedTypes: ['character', 'object'],
        fieldMapping: { referenceImagesField: 'referenceImages' },
      },
    } as VideoModelOptions,
  },
  {
    id: 'gemini-omni-flash-image',
    title: 'Gemini Omni Flash Image',
    description:
      'Transform static images into 720p videos up to 10 seconds with native audio using Gemini Omni Flash, guided by conversational prompts.',
    model: 'gemini-omni-flash-preview',
    isNew: false,
    isBest: true,
    credits: 60, // Fallback flat price (10s) when no duration is passed — 6 credits/s, Google's own 720p Omni rate (see GEMINI_OMNI_11_CREDITS_PER_SECOND)
    needsEndpointSuffix: false,
    icon: GoogleSvg,
    options: {
      creditsPerSecond: GEMINI_OMNI_11_CREDITS_PER_SECOND,
      duration: ['4', '5', '6', '7', '8', '9', '10'],
      aspectRatio: [],
      inputType: ['image'],
      generatesAudio: true,
    } as VideoModelOptions,
  },
  {
    id: 'gemini-omni-flash-11-image',
    title: 'Gemini Omni Flash 1.1 Image',
    description:
      'Animate a still image with Gemini Omni 1.1 Flash — 3-10 second clips from 360p drafts up to 4K, with native audio, guided by conversational prompts.',
    model: GEMINI_OMNI_11_MODEL_ID,
    isNew: true,
    isBest: true,
    credits: GEMINI_OMNI_11_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: GoogleSvg,
    landingPage: '/ai-video-generator/gemini-omni-1-1-flash',
    options: {
      creditsPerSecond: GEMINI_OMNI_11_CREDITS_PER_SECOND,
      duration: GEMINI_OMNI_11_DURATIONS,
      defaultDuration: GEMINI_OMNI_11_DEFAULT_DURATION,
      // The source image drives the output framing, as on the preview entry.
      aspectRatio: [],
      inputType: ['image'],
      generatesAudio: true,
      // The interactions API has no audio field at all — every render comes
      // back with a native stereo track (verified on all four flows). Without
      // this the UI offers an audio toggle that silently does nothing, and the
      // video orchestrator leaves the model's own dialogue and score playing
      // underneath a generated voiceover. No discount is lost: these entries
      // set no audioDisabledCreditsMultiplier, so "off" never cost less.
      alwaysGeneratesAudio: true,
      resolution: GEMINI_OMNI_11_RESOLUTIONS,
      defaultResolution: GEMINI_OMNI_11_DEFAULT_RESOLUTION,
      resolutionCreditsMultiplier: GEMINI_OMNI_11_RESOLUTION_MULTIPLIER,
    } as VideoModelOptions,
  },
  {
    id: 'gemini-omni-flash-11-first-last-frame',
    title: 'Gemini Omni Flash 1.1 First-Last Frame',
    description:
      'Interpolate between a first and last frame with Gemini Omni 1.1 Flash — smooth transitions with native audio, from 360p drafts up to 4K.',
    model: GEMINI_OMNI_11_MODEL_ID,
    isNew: true,
    credits: GEMINI_OMNI_11_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: GoogleSvg,
    landingPage: '/ai-video-generator/gemini-omni-1-1-flash',
    options: {
      creditsPerSecond: GEMINI_OMNI_11_CREDITS_PER_SECOND,
      duration: GEMINI_OMNI_11_DURATIONS,
      defaultDuration: GEMINI_OMNI_11_DEFAULT_DURATION,
      aspectRatio: [],
      inputType: ['image'],
      // Both frames ride the interactions API's `input` array as ordered
      // images (first, then last) — there is no separate last-frame field, so
      // `tailImageFieldName` is deliberately unset; the Omni branch in
      // /api/generate-video appends the tail image itself.
      supportsTailImage: true,
      generatesAudio: true,
      // The interactions API has no audio field at all — every render comes
      // back with a native stereo track (verified on all four flows). Without
      // this the UI offers an audio toggle that silently does nothing, and the
      // video orchestrator leaves the model's own dialogue and score playing
      // underneath a generated voiceover. No discount is lost: these entries
      // set no audioDisabledCreditsMultiplier, so "off" never cost less.
      alwaysGeneratesAudio: true,
      resolution: GEMINI_OMNI_11_RESOLUTIONS,
      defaultResolution: GEMINI_OMNI_11_DEFAULT_RESOLUTION,
      resolutionCreditsMultiplier: GEMINI_OMNI_11_RESOLUTION_MULTIPLIER,
    } as VideoModelOptions,
  },
  {
    id: 'minimax-h3-max-image',
    title: 'MiniMax H3 Max Image',
    description:
      'Animate a photo with the speed-tuned MiniMax H3 Max — 480P/768P, 5-15 seconds, native audio, and an optional last frame for first-to-last keyframe control.',
    model: 'minimax/h3-max/image-to-video',
    isNew: true,
    isFast: true,
    credits: MINIMAX_H3_MAX_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: HailuoSvg,
    options: {
      maxPromptLength: 4000,
      creditsPerSecond: MINIMAX_H3_MAX_CREDITS_PER_SECOND,
      duration: MINIMAX_H3_MAX_DURATIONS,
      // The output follows the start image's own aspect ratio and the
      // endpoint takes no `aspect_ratio` — empty hides the picker AND stops
      // the fal branch from sending the field.
      aspectRatio: [],
      resolution: MINIMAX_H3_MAX_RESOLUTIONS,
      resolutionCreditsMultiplier: MINIMAX_H3_MAX_RESOLUTION_MULTIPLIER,
      inputType: ['image'],
      generatesAudio: true,
      alwaysGeneratesAudio: true,
      // First-to-last keyframe generation: the same endpoint takes an
      // optional final frame, under `end_image_url` rather than the
      // catalogue's usual `tail_image_url` default.
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
    } as VideoModelOptions,
  },
  {
    id: 'grok-imagine-image',
    title: 'Grok Imagine Image',
    description:
      'xAI Grok Imagine video model for image-to-video generation with high-quality output',
    model: 'grok-imagine-video',
    isNew: false,
    isBest: true,
    credits: 10, // Default display (5s × 2 @ 480p)
    needsEndpointSuffix: false,
    icon: XAISvg,
    options: {
      creditsPerSecond: 2, // 480p baseline; 720p via resolutionCreditsMultiplier
      duration: ['5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
      aspectRatio: [],
      inputType: ['image'],
      resolution: ['480p', '720p'],
      resolutionCreditsMultiplier: { '720p': 1.5 }, // 480p 2/s, 720p 3/s
    } as VideoModelOptions,
  },
  {
    id: 'grok-imagine-image-1.5',
    title: 'Grok Imagine 1.5',
    description:
      'xAI Grok Imagine Video 1.5 — image-to-video with sharper motion, synchronized native audio, and up to 1080p output.',
    model: 'grok-imagine-video-1.5',
    isNew: true,
    isBest: true,
    credits: 20, // Default display (5s × 4 @ 480p)
    needsEndpointSuffix: false,
    icon: XAISvg,
    options: {
      creditsPerSecond: 4, // 480p baseline; higher res via resolutionCreditsMultiplier
      duration: ['5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
      aspectRatio: [],
      inputType: ['image'],
      resolution: ['480p', '720p', '1080p'],
      resolutionCreditsMultiplier: { '720p': 1.75, '1080p': 3.125 }, // 480p 4/s ($0.08), 720p 7/s ($0.14), 1080p 12.5/s ($0.25)
      generatesAudio: true,
      referenceSiblingId: 'grok-imagine-reference-1.5',
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-image',
    title: 'Seedance 2.0 Image',
    description:
      'Transform images into cinematic videos with native audio, real-world physics, and director-level camera control.',
    model: 'bytedance/seedance-2.0/image-to-video',
    isNew: false,
    isBest: true,
    isEnterprise: false,
    credits: 60, // Default display (5s × 12 @ 720p)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2',
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 12, // WaveSpeed 720p: $0.24/s ÷ $0.02/credit
      duration: [
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: ['480p', '720p', '1080p', '4k'],
      resolutionCreditsMultiplier: { '480p': 0.5, '1080p': 2.5, '4k': 5 },
      inputType: ['image'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-fast-image',
    title: 'Seedance 2.0 Fast Image',
    description:
      'Fast image-to-video with cinematic output, native audio, and real-world physics at reduced generation times.',
    model: 'bytedance/seedance-2.0/fast/image-to-video',
    isNew: false,
    isEnterprise: false,
    credits: 30, // Default display (5s × 6 @ 720p)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2',
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 6, // 6 credits/s @ 720p (480p ×0.5 -> 3/s); WaveSpeed 480p ≈ $0.09/s
      duration: [
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      // Seedance 2.0 Fast supports the full resolution ladder incl. 4K
      // (480p ×0.5, 1080p ×2.5, 4K ×5 — same cost curve as the 2.0 family).
      resolution: ['480p', '720p', '1080p', '4k'],
      resolutionCreditsMultiplier: { '480p': 0.5, '1080p': 2.5, '4k': 5 },
      inputType: ['image'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
    } as VideoModelOptions,
  },
  {
    id: 'minimax-h3-image',
    title: 'MiniMax H3 Image',
    description:
      'Animate a first-frame image into a coherent 768P/2K video with native audio, natural-language motion instructions, and optional last-frame control.',
    model: 'minimax/h3/image-to-video',
    isNew: true,
    isBest: true,
    credits: MINIMAX_H3_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: HailuoSvg,
    landingPage: '/ai-video-generator/minimax-h3',
    options: {
      maxPromptLength: 4000,
      creditsPerSecond: MINIMAX_H3_CREDITS_PER_SECOND,
      duration: MINIMAX_H3_DURATIONS,
      // Output ratio follows the input image (image-to-video is always
      // `ratio: adaptive`); a single entry keeps the selector hidden.
      aspectRatio: ['16:9'],
      resolution: MINIMAX_H3_RESOLUTIONS,
      resolutionCreditsMultiplier: MINIMAX_H3_RESOLUTION_MULTIPLIER,
      inputType: ['image'],
      generatesAudio: true,
      alwaysGeneratesAudio: true,
      supportsTailImage: true,
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-5-turbo-image',
    title: 'Seedance 2.5 Turbo Image',
    description:
      'Fast image-to-video with Seedance 2.5 — animate a photo into a take of up to 30 seconds with native audio, at the turbo tier’s lower price.',
    model: 'bytedance/seedance-2.5/image-to-video-turbo',
    isNew: true,
    isEnterprise: false,
    credits: SEEDANCE_25_TURBO_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2-5',
    options: {
      maxPromptLength: SEEDANCE_25_MAX_PROMPT_LENGTH,
      creditsPerSecond: SEEDANCE_25_TURBO_CREDITS_PER_SECOND,
      duration: SEEDANCE_25_DURATIONS,
      // Framing follows the start image; a single entry hides the selector.
      aspectRatio: ['16:9'],
      resolution: SEEDANCE_25_TURBO_RESOLUTIONS,
      resolutionCreditsMultiplier: SEEDANCE_25_TURBO_RESOLUTION_MULTIPLIER,
      inputType: ['image'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsTailImage: true,
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-5-image',
    title: 'Seedance 2.5 Image',
    description:
      'Animate a photo into a Seedance 2.5 take of up to 30 seconds with native audio — optionally locking the closing frame with an end image.',
    model: 'bytedance/seedance-2.5/image-to-video',
    isNew: true,
    isBest: true,
    isEnterprise: false,
    credits: SEEDANCE_25_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2-5',
    options: {
      maxPromptLength: SEEDANCE_25_MAX_PROMPT_LENGTH,
      creditsPerSecond: SEEDANCE_25_CREDITS_PER_SECOND,
      duration: SEEDANCE_25_DURATIONS,
      // First-frame and first-last-frame runs are always `ratio: adaptive` —
      // the output keeps the start image's framing. A single entry keeps the
      // aspect-ratio selector hidden rather than showing a control that lies.
      aspectRatio: ['16:9'],
      resolution: SEEDANCE_25_RESOLUTIONS,
      resolutionCreditsMultiplier: SEEDANCE_25_RESOLUTION_MULTIPLIER,
      inputType: ['image'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsTailImage: true,
    } as VideoModelOptions,
  },
  {
    id: 'flux-3-image',
    title: 'FLUX 3 Image',
    description:
      'Animate a photo with FLUX 3 — a 5-20 second take that keeps the subject intact while the prompt drives motion, camera work, and style, with synchronized audio and an optional closing frame.',
    model: 'black-forest-labs/flux-3/image-to-video',
    isNew: true,
    isBest: true,
    isEnterprise: false,
    credits: FLUX_3_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: FluxSvg,
    options: {
      creditsPerSecond: FLUX_3_CREDITS_PER_SECOND,
      duration: FLUX_3_DURATIONS,
      aspectRatio: FLUX_3_ASPECT_RATIOS,
      resolution: FLUX_3_RESOLUTIONS,
      resolutionCreditsMultiplier: FLUX_3_RESOLUTION_MULTIPLIER,
      inputType: ['image'],
      generatesAudio: true,
      supportsAudioToggle: true,
      // Adding a closing frame moves the job to FLUX 3's separate
      // start-end-to-video endpoint (same price) — handled in
      // WaveSpeedVideoService.submitSeedanceVideo.
      supportsTailImage: true,
    } as VideoModelOptions,
  },
  {
    id: 'flux-3-max-image',
    title: 'FLUX 3 Max Image',
    description:
      'Animate a photo with FLUX 3 Max — tighter prompt adherence and better visual quality than FLUX 3, 5-20 seconds with synchronized audio, and an optional closing frame.',
    model: 'blackforestlabs/flux-3-max/image-to-video',
    isNew: true,
    isBest: true,
    isEnterprise: false,
    get credits() {
      return flux3MaxPricing().credits;
    },
    get isPromo() {
      return flux3MaxPricing().promo;
    },
    needsEndpointSuffix: false,
    icon: FluxSvg,
    options: {
      get creditsPerSecond() {
        return flux3MaxPricing().creditsPerSecond;
      },
      get resolutionCreditsMultiplier() {
        return flux3MaxPricing().resolutionCreditsMultiplier;
      },
      duration: FLUX_3_DURATIONS,
      aspectRatio: FLUX_3_ASPECT_RATIOS,
      resolution: FLUX_3_RESOLUTIONS,
      inputType: ['image'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
    } as VideoModelOptions,
  },
  {
    id: 'wan-3-image',
    title: 'WAN 3.0 Image',
    description:
      'Animate a photo into a WAN 3.0 take of up to 30 seconds at native 1080p with scored audio — optionally locking the closing frame with an end image.',
    model: 'alibaba/wan-3.0/image-to-video',
    isNew: true,
    isBest: true,
    isEnterprise: false,
    credits: WAN_3_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: WanSvg,
    landingPage: '/ai-video-generator/wan-3-0',
    options: {
      maxPromptLength: WAN_3_MAX_PROMPT_LENGTH,
      creditsPerSecond: WAN_3_CREDITS_PER_SECOND,
      duration: WAN_3_DURATIONS,
      // WAN 3.0's image endpoint honours a concrete aspect ratio, so the
      // selector stays live here (Seedance 2.5 Image forces `adaptive` and
      // collapses to a single entry to hide a control that would lie).
      aspectRatio: WAN_3_ASPECT_RATIOS,
      resolution: WAN_3_RESOLUTIONS,
      resolutionCreditsMultiplier: WAN_3_RESOLUTION_MULTIPLIER,
      inputType: ['image'],
      generatesAudio: true,
      supportsAudioToggle: true,
      // Last-frame guidance is a field on the same endpoint (`last_image`),
      // the Seedance convention — not FLUX 3's separate start-end endpoint.
      supportsTailImage: true,
    } as VideoModelOptions,
  },
  {
    id: 'kling-40-image',
    title: 'Kling 4.0 Image',
    description:
      'Animate a photo into a Kling 4.0 take of 3 to 30 seconds with synchronized stereo audio — optionally locking the closing frame with an end image.',
    model: 'kwaivgi/kling-v4.0/image-to-video',
    isNew: true,
    isBest: true,
    isEnterprise: false,
    credits: KLING_40_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: KlingSvg,
    landingPage: '/ai-video-generator/kling-4-0',
    options: {
      maxPromptLength: KLING_40_MAX_PROMPT_LENGTH,
      creditsPerSecond: KLING_40_CREDITS_PER_SECOND,
      duration: KLING_40_DURATIONS,
      defaultDuration: KLING_40_DEFAULT_DURATION,
      // The endpoint follows the first frame when `aspect_ratio` is left
      // out, which the builder always does; a single entry hides the
      // selector rather than offering a crop the user didn't ask for.
      aspectRatio: ['16:9'],
      resolution: KLING_40_RESOLUTIONS,
      resolutionCreditsMultiplier: KLING_40_RESOLUTION_MULTIPLIER,
      inputType: ['image'],
      generatesAudio: true,
      alwaysGeneratesAudio: true,
      // `last_image` on the same endpoint, the Seedance convention.
      supportsTailImage: true,
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-turbo-image',
    title: 'Seedance 2.0 Turbo Image',
    description:
      'Transform images into 720p/1080p cinematic videos at near-480p speed with native audio and director-level control.',
    model: 'bytedance/seedance-2.0/image-to-video-turbo',
    isNew: false,
    isBest: true,
    isEnterprise: false,
    credits: 35, // Default display (5s × 7 @ 720p)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2',
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 7, // WaveSpeed 720p: $0.14/s ÷ $0.02/credit
      duration: [
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: ['720p', '1080p'],
      resolutionCreditsMultiplier: { '1080p': 1.07 }, // 1080p costs ~$0.15/s vs $0.14/s
      inputType: ['image'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
    } as VideoModelOptions,
  },
  {
    id: 'seedance-2-mini-image',
    // Free-plan users may use the Mini family (480p, <=5s) — enforced in
    // /api/generate-video; the welcome gift makes the first video affordable.
    freeUserAccess: true,
    freePlanPolicy: {
      resolutions: ['480p'] as ResolutionType[],
      maxDurationSeconds: 5,
    },
    title: 'Seedance 2.0 Mini Image',
    description:
      'Transform images into cinematic videos fast and affordably, with native audio and director-level camera control.',
    model: 'bytedance/seedance-2.0-mini/image-to-video',
    isNew: false,
    isEnterprise: false,
    credits: 20, // Default display (5s × 4 @ 720p)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2',
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 4, // 480p = 2 credits/s (×0.5 multiplier); subsidized promo rate below WaveSpeed cost
      duration: [
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
      resolution: ['480p', '720p', '1080p', '4k'],
      resolutionCreditsMultiplier: { '480p': 0.5, '1080p': 2.5, '4k': 5 },
      inputType: ['image'],
      generatesAudio: true,
      supportsAudioToggle: true,
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
    } as VideoModelOptions,
  },
  {
    id: 'happyhorse-text',
    title: 'Happy Horse',
    description:
      "Alibaba's #1-ranked text-to-video model with synchronized native audio, multilingual lip-sync, and cinematic motion. Up to 1080p.",
    model: 'alibaba/happy-horse/text-to-video',
    isNew: false,
    isBest: true,
    credits: 35, // Default display (5s × 7 @ 720p)
    needsEndpointSuffix: false,
    icon: AlibabaSvg,
    options: {
      maxPromptLength: 2500,
      creditsPerSecond: 7,
      duration: [
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4'],
      inputType: ['text'],
      generatesAudio: true,
      resolution: ['720p', '1080p'],
      resolutionCreditsMultiplier: { '1080p': 2 },
    } as VideoModelOptions,
  },
  {
    id: 'happyhorse-image',
    title: 'Happy Horse Image',
    description:
      'Image-to-video with synchronized native audio. Animates a first-frame image into 1080p video with cinematic motion.',
    model: 'alibaba/happy-horse/image-to-video',
    isNew: false,
    isBest: true,
    credits: 35, // Default display (5s × 7 @ 720p)
    needsEndpointSuffix: false,
    icon: AlibabaSvg,
    options: {
      maxPromptLength: 2500,
      creditsPerSecond: 7,
      duration: [
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      // Image-to-video derives aspect ratio from the input image — no aspect_ratio param.
      aspectRatio: [],
      inputType: ['image'],
      generatesAudio: true,
      resolution: ['720p', '1080p'],
      resolutionCreditsMultiplier: { '1080p': 2 },
    } as VideoModelOptions,
  },
  {
    id: 'happyhorse-reference',
    title: 'Happy Horse Reference',
    description:
      'Reference-to-video with up to 9 reference images for consistent characters and objects, synchronized native audio, and cinematic motion.',
    model: 'alibaba/happy-horse/reference-to-video',
    isNew: false,
    isBest: true,
    credits: 35, // Default display (5s × 7 @ 720p)
    needsEndpointSuffix: false,
    icon: AlibabaSvg,
    options: {
      maxPromptLength: 2500,
      creditsPerSecond: 7,
      duration: [
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1', '4:3', '3:4'],
      inputType: ['text'],
      generatesAudio: true,
      resolution: ['720p', '1080p'],
      resolutionCreditsMultiplier: { '1080p': 2 },
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      referenceImageConfig: {
        maxImages: 9,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {
          imageUrlsField: 'image_urls',
        },
      },
    } as VideoModelOptions,
  },
  {
    id: 'kling-26-pro-image',
    title: 'Kling 2.6 Pro Image',
    description:
      'Advanced image-to-video transformation with exceptional quality and cinematic motion dynamics',
    model: 'fal-ai/kling-video/v2.6/pro/image-to-video',
    isNew: false,
    isBest: true,
    credits: 30,
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      duration: ['5', '10'],
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['image'],
      generatesAudio: true,
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
    } as VideoModelOptions,
  },
  {
    id: 'kling-30-pro-text',
    title: 'Kling 3.0 Pro',
    description:
      'Top-tier text-to-video with cinematic visuals, fluid motion, and native audio generation, with multi-shot support.',
    model: 'fal-ai/kling-video/v3/pro/text-to-video',
    isNew: false,
    isBest: true,
    credits: 75, // Default display (5s × 15)
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      creditsPerSecond: 15,
      duration: [
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['text'],
      generatesAudio: true,
      audioDisabledCreditsMultiplier: 0.75,
    } as VideoModelOptions,
  },
  {
    id: 'kling-30-pro-image',
    title: 'Kling 3.0 Pro Image',
    description:
      'Top-tier image-to-video with cinematic visuals, fluid motion, and native audio generation.',
    model: 'fal-ai/kling-video/v3/pro/image-to-video',
    isNew: false,
    isBest: true,
    credits: 75, // Default display (5s × 15)
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      creditsPerSecond: 15,
      duration: [
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['image'],
      generatesAudio: true,
      audioDisabledCreditsMultiplier: 0.75,
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
    } as VideoModelOptions,
  },
  {
    id: 'kling-30-standard-text',
    title: 'Kling 3.0 Standard',
    description:
      'Text-to-video with cinematic visuals, fluid motion, and native audio generation.',
    model: 'fal-ai/kling-video/v3/standard/text-to-video',
    isNew: false,
    credits: 50, // Default display (5s × 10)
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      creditsPerSecond: 10,
      duration: [
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['text'],
      generatesAudio: true,
      audioDisabledCreditsMultiplier: 0.75,
    } as VideoModelOptions,
  },
  {
    id: 'kling-30-standard-image',
    title: 'Kling 3.0 Standard Image',
    description:
      'Image-to-video with cinematic visuals, fluid motion, and native audio generation.',
    model: 'fal-ai/kling-video/v3/standard/image-to-video',
    isNew: false,
    credits: 50, // Default display (5s × 10)
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      creditsPerSecond: 10,
      duration: [
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['image'],
      generatesAudio: true,
      audioDisabledCreditsMultiplier: 0.75,
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
    } as VideoModelOptions,
  },
  {
    id: 'kling-o3-pro-text',
    title: 'Kling O3 Pro',
    description:
      'Kling Omni 3 Pro — reference-heavy workflows with character consistency, native audio, multi-shot, and voice control.',
    model: 'fal-ai/kling-video/o3/pro/text-to-video',
    isNew: false,
    isBest: true,
    credits: 35, // Default display (5s × 7)
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      creditsPerSecond: 7,
      duration: [
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['text'],
      generatesAudio: true,
      audioDisabledCreditsMultiplier: 0.75,
      // O3 Pro has no own reference endpoint; its reference sibling is the
      // standard O3 Reference model (cheaper tier), resolved explicitly here.
      referenceSiblingId: 'kling-o3-reference',
    } as VideoModelOptions,
  },
  {
    id: 'kling-o3-pro-image',
    title: 'Kling O3 Pro Image',
    description:
      'Kling Omni 3 Pro — image-to-video with native audio, multi-shot, and voice control.',
    model: 'fal-ai/kling-video/o3/pro/image-to-video',
    isNew: false,
    isBest: true,
    credits: 35, // Default display (5s × 7)
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      creditsPerSecond: 7,
      duration: [
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['image'],
      generatesAudio: true,
      audioDisabledCreditsMultiplier: 0.75,
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
    } as VideoModelOptions,
  },
  {
    id: 'kling-o3-standard-text',
    title: 'Kling O3 Standard',
    description:
      'Kling Omni 3 — text-to-video with native audio, multi-shot support, and character consistency.',
    model: 'fal-ai/kling-video/o3/standard/text-to-video',
    isNew: false,
    credits: 25, // Default display (5s × 5)
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      creditsPerSecond: 5,
      duration: [
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['text'],
      generatesAudio: true,
      audioDisabledCreditsMultiplier: 0.75,
    } as VideoModelOptions,
  },
  {
    id: 'kling-o3-standard-image',
    title: 'Kling O3 Standard Image',
    description:
      'Kling Omni 3 — image-to-video with native audio, multi-shot support, and character consistency.',
    model: 'fal-ai/kling-video/o3/standard/image-to-video',
    isNew: false,
    credits: 25, // Default display (5s × 5)
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      creditsPerSecond: 5,
      duration: [
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['image'],
      generatesAudio: true,
      audioDisabledCreditsMultiplier: 0.75,
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
    } as VideoModelOptions,
  },
  {
    id: 'kling-o3-reference',
    title: 'Kling O3 Reference',
    description:
      'Kling Omni 3 — generate videos with consistent characters and objects using reference images, with native audio.',
    model: 'fal-ai/kling-video/o3/standard/reference-to-video',
    isNew: false,
    credits: 25, // Default display (5s × 5)
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      creditsPerSecond: 5,
      duration: [
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
      ],
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['text'],
      generatesAudio: true,
      audioDisabledCreditsMultiplier: 0.75,
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      referenceImageConfig: {
        maxImages: 4,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {
          elementsField: 'elements',
          imageUrlsField: 'image_urls',
        },
      },
    } as VideoModelOptions,
  },
  {
    id: 'seedance-15-pro-text',
    title: 'Seedance 1.5 Pro',
    description:
      "ByteDance's latest Seedance 1.5 Pro with synchronized audio generation and premium quality",
    model: 'bytedance/seedance-v1.5-pro/text-to-video',
    isNew: false,
    isBest: true,
    credits: 20, // Default display (5s × 4 @ 720p audio on)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2',
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 4, // 720p baseline; see resolutionCreditsMultiplier for 480p/1080p
      duration: ['5', '6', '7', '8', '9', '10', '11', '12', '4'],
      aspectRatio: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'],
      resolution: ['480p', '720p', '1080p'],
      resolutionCreditsMultiplier: { '480p': 0.5, '1080p': 2 },
      inputType: ['text'],
      generatesAudio: true,
      audioDisabledCreditsMultiplier: 0.5,
    } as VideoModelOptions,
  },
  {
    id: 'seedance-15-pro-image',
    title: 'Seedance 1.5 Pro Image',
    description:
      "Transform images into videos with ByteDance's Seedance 1.5 Pro and synchronized audio",
    model: 'bytedance/seedance-v1.5-pro/image-to-video',
    isNew: false,
    isBest: true,
    credits: 20, // Default display (5s × 4 @ 720p audio on)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    landingPage: '/ai-video-generator/seedance-2',
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 4, // 720p baseline; see resolutionCreditsMultiplier for 480p/1080p
      duration: ['5', '6', '7', '8', '9', '10', '11', '12', '4'],
      aspectRatio: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'],
      resolution: ['480p', '720p', '1080p'],
      resolutionCreditsMultiplier: { '480p': 0.5, '1080p': 2 },
      inputType: ['image'],
      generatesAudio: true,
      audioDisabledCreditsMultiplier: 0.5,
    } as VideoModelOptions,
  },
  {
    id: 'kling-motion-control-pro',
    title: 'Kling Motion Control Pro',
    description:
      'Control character motion with a reference video. Animate any character image using motion from your video. 1080p quality.',
    model: 'fal-ai/kling-video/v2.6/pro/motion-control',
    isNew: true,
    isBest: true,
    credits: 5, // Base rate per second (5 credits/sec)
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['image'],
      isMotionControl: true,
      generatesAudio: true, // Skip audio generation (reference video may have audio)
    } as VideoModelOptions,
  },
  {
    id: 'kling-motion-control-standard',
    title: 'Kling Motion Control',
    description:
      'Control character motion with a reference video. Animate any character image using motion from your video. 720p quality.',
    model: 'fal-ai/kling-video/v2.6/standard/motion-control',
    isNew: true,
    credits: 2.5, // Base rate per second (2.5 credits/sec)
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['image'],
      isMotionControl: true,
      generatesAudio: true, // Skip audio generation (reference video may have audio)
    } as VideoModelOptions,
  },
  {
    id: 'kling-o1-image',
    title: 'Kling O1 Image',
    description:
      "The world's first unified multimodal video model — a brand-new creative engine to unlock endless creative possibilities",
    model: 'fal-ai/kling-video/o1/image-to-video',
    isNew: false,
    isBest: true,
    credits: 25,
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      duration: ['5', '10'],
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
      imageFieldName: 'start_image_url',
      aspectRatio: ['1:1'],
      inputType: ['image'],
    } as VideoModelOptions,
  },
  {
    id: 'kling-o1-reference',
    title: 'Kling O1 Reference',
    description:
      'Generate videos with consistent characters and objects using reference images - no start image required',
    model: 'fal-ai/kling-video/o1/reference-to-video',
    isNew: false,
    isBest: true,
    credits: 25,
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      duration: ['5', '10', '3'],
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['text'],
      supportsReferenceImages: true,
      requiresReferenceImages: true,
      referenceImageConfig: {
        maxImages: 4,
        supportedTypes: ['character', 'object', 'style'],
        fieldMapping: {
          elementsField: 'elements',
          imageUrlsField: 'image_urls',
        },
      },
    } as VideoModelOptions,
  },
  {
    id: 'kling-25-turbo-pro-image',
    title: 'Kling 2.5 Turbo Pro Image',
    description:
      'Premium image-to-video transformation with superior motion dynamics and cinematic quality',
    model: 'fal-ai/kling-video/v2.5-turbo/pro/image-to-video',
    isNew: false,
    isBest: true,
    credits: 18,
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      duration: ['5', '10'],
      supportsTailImage: true,
      tailImageFieldName: 'tail_image_url',
      aspectRatio: ['16:9'],
      inputType: ['image'],
    } as VideoModelOptions,
  },
  {
    id: 'kling-25-turbo-pro-text',
    title: 'Kling 2.5 Turbo Pro',
    description:
      'Premium text-to-video generation with exceptional quality, advanced motion control and professional-grade output',
    model: 'fal-ai/kling-video/v2.5-turbo/pro/text-to-video',
    isNew: false,
    isBest: true,
    credits: 18,
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      duration: ['5', '10'],
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['text'],
    } as VideoModelOptions,
  },
  {
    id: 'kling-25-turbo-standard-image',
    title: 'Kling 2.5 Turbo Standard',
    description:
      'Top-tier image-to-video generation with unparalleled motion fluidity, cinematic visuals, and exceptional prompt precision',
    model: 'fal-ai/kling-video/v2.5-turbo/standard/image-to-video',
    isNew: false,
    credits: 10,
    needsEndpointSuffix: false,
    icon: KlingSvg,
    options: {
      maxPromptLength: 2500,
      duration: ['5', '10'],
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['image'],
    } as VideoModelOptions,
  },
  {
    id: 'seedance-15-pro-first-last-frame',
    title: 'Seedance 1.5 Pro First-Last Frame',
    description:
      "Create videos from first and last frame images with ByteDance's Seedance 1.5 Pro",
    model: 'bytedance/seedance-v1.5-pro/image-to-video',
    isNew: false,
    isBest: true,
    credits: 20, // Default display (5s × 4 @ 720p audio on)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 4, // 720p baseline; see resolutionCreditsMultiplier for 480p/1080p
      duration: ['5', '6', '7', '8', '9', '10', '11', '12', '4'],
      aspectRatio: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'],
      resolution: ['480p', '720p', '1080p'],
      resolutionCreditsMultiplier: { '480p': 0.5, '1080p': 2 },
      inputType: ['image'],
      supportsTailImage: true,
      tailImageFieldName: 'last_frame',
      generatesAudio: true,
      audioDisabledCreditsMultiplier: 0.5,
    } as VideoModelOptions,
  },
  {
    id: 'veo31-text',
    title: 'Veo 3.1',
    description:
      'Latest Google Veo 3.1 model with exceptional text-to-video generation and up to 4K resolution',
    model: 'veo-3.1-generate-001',
    isNew: false,
    isBest: true,
    credits: VEO_31_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: GoogleSvg,
    options: {
      maxPromptLength: 3500,
      creditsPerSecond: VEO_31_CREDITS_PER_SECOND,
      duration: ['8s', '6s', '4s'],
      aspectRatio: ['16:9', '9:16'],
      inputType: ['text'],
      generatesAudio: true,
      supportsAudioToggle: true,
      audioDisabledCreditsMultiplier: VEO_31_AUDIO_OFF_MULTIPLIER,
      resolution: ['720p', '1080p', '4k'],
      resolutionCreditsMultiplier: VEO_31_RESOLUTION_MULTIPLIER,
      supportsReferenceImages: true,
      referenceImageConfig: {
        maxImages: 3,
        supportedTypes: ['character', 'object'],
        fieldMapping: { referenceImagesField: 'referenceImages' },
      },
    } as VideoModelOptions,
  },

  {
    id: 'ltxv-2-text',
    title: 'LTX-2 Pro',
    description:
      'Premium text-to-video generation with superior quality and cinematic output',
    model: 'fal-ai/ltxv-2/text-to-video',
    isNew: false,
    isBest: true,
    credits: 18, // Default display (6s × 3)
    needsEndpointSuffix: false,
    icon: LTXVSvg,
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 3,
      duration: ['6', '8', '10'],
      aspectRatio: ['16:9'],
      inputType: ['text'],
      generatesAudio: true,
    } as VideoModelOptions,
  },
  {
    id: 'ltxv-2-text-fast',
    title: 'LTX-2 Fast',
    description:
      'Fast text-to-video generation with flexible duration options up to 20 seconds and multiple resolutions',
    model: 'fal-ai/ltxv-2/text-to-video/fast',
    isNew: false,
    isBest: true,
    credits: 20, // Default display (10s × 2)
    needsEndpointSuffix: false,
    icon: LTXVSvg,
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 2,
      duration: ['10', '6', '8', '12', '14', '16', '18', '20'],
      aspectRatio: ['16:9'],
      inputType: ['text'],
      generatesAudio: true,
    } as VideoModelOptions,
  },
  {
    id: 'veo31-image',
    title: 'Veo 3.1 Image',
    description:
      'Transform images into videos with Google Veo 3.1 in up to 4K resolution',
    model: 'veo-3.1-generate-001',
    isNew: false,
    isBest: true,
    credits: VEO_31_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: GoogleSvg,
    options: {
      maxPromptLength: 3500,
      creditsPerSecond: VEO_31_CREDITS_PER_SECOND,
      duration: ['8s'],
      aspectRatio: ['16:9', '9:16'],
      inputType: ['image'],
      generatesAudio: true,
      supportsAudioToggle: true,
      audioDisabledCreditsMultiplier: VEO_31_AUDIO_OFF_MULTIPLIER,
      resolution: ['720p', '1080p', '4k'],
      resolutionCreditsMultiplier: VEO_31_RESOLUTION_MULTIPLIER,
      supportsReferenceImages: true,
      referenceImageConfig: {
        maxImages: 3,
        supportedTypes: ['character', 'object'],
        fieldMapping: { referenceImagesField: 'referenceImages' },
      },
    } as VideoModelOptions,
  },
  {
    id: 'veo31-first-last-frame',
    title: 'Veo 3.1 First-Last Frame',
    description:
      'Create videos from first and last frame images with Google Veo 3.1 in up to 4K resolution',
    model: 'veo-3.1-generate-001',
    isNew: false,
    isBest: true,
    credits: VEO_31_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: GoogleSvg,
    options: {
      maxPromptLength: 3500,
      creditsPerSecond: VEO_31_CREDITS_PER_SECOND,
      duration: ['8s'],
      aspectRatio: ['16:9', '9:16'],
      inputType: ['image'],
      supportsTailImage: true,
      tailImageFieldName: 'last_frame_url',
      generatesAudio: true,
      supportsAudioToggle: true,
      audioDisabledCreditsMultiplier: VEO_31_AUDIO_OFF_MULTIPLIER,
      resolution: ['720p', '1080p', '4k'],
      resolutionCreditsMultiplier: VEO_31_RESOLUTION_MULTIPLIER,
      supportsReferenceImages: true,
      referenceImageConfig: {
        maxImages: 3,
        supportedTypes: ['character', 'object'],
        fieldMapping: { referenceImagesField: 'referenceImages' },
      },
    } as VideoModelOptions,
  },
  {
    id: 'veo31-fast-first-last-frame',
    // Veo runs on Vertex AI (covered by GCP credits). Free users pay the
    // standard credit prices, capped at 720p. Enforced in /api/generate-video.
    freeUserAccess: true,
    freePlanPolicy: { resolutions: ['720p'] as ResolutionType[] },
    title: 'Veo 3.1 Fast First-Last Frame',
    description:
      'Fast version: Create videos from first and last frame images with Google Veo 3.1 in up to 4K',
    model: 'veo-3.1-fast-generate-001',
    isNew: false,
    isBest: true,
    credits: VEO_31_FAST_DEFAULT_CREDITS,
    needsEndpointSuffix: false,
    icon: GoogleSvg,
    options: {
      maxPromptLength: 3500,
      creditsPerSecond: VEO_31_FAST_CREDITS_PER_SECOND,
      duration: ['8s'],
      aspectRatio: ['16:9', '9:16'],
      inputType: ['image'],
      supportsTailImage: true,
      tailImageFieldName: 'last_frame_url',
      generatesAudio: true,
      supportsAudioToggle: true,
      audioDisabledCreditsMultiplier: VEO_31_FAST_AUDIO_OFF_MULTIPLIER,
      resolution: ['720p', '1080p', '4k'],
      resolutionCreditsMultiplier: VEO_31_FAST_RESOLUTION_MULTIPLIER,
      supportsReferenceImages: true,
      referenceImageConfig: {
        maxImages: 3,
        supportedTypes: ['character', 'object'],
        fieldMapping: { referenceImagesField: 'referenceImages' },
      },
    } as VideoModelOptions,
  },
  {
    id: 'ltxv-2-image',
    title: 'LTX-2 Pro Image',
    description:
      'Premium image-to-video transformation with exceptional quality and realistic motion',
    model: 'fal-ai/ltxv-2/image-to-video',
    isNew: false,
    isBest: true,
    credits: 18, // Default display (6s × 3)
    needsEndpointSuffix: false,
    icon: LTXVSvg,
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 3,
      duration: ['6', '8', '10'],
      aspectRatio: ['16:9'],
      inputType: ['image'],
      generatesAudio: true,
    } as VideoModelOptions,
  },
  {
    id: 'ltxv-2-image-fast',
    title: 'LTX-2 Fast Image',
    description:
      'Fast image-to-video transformation with flexible duration options up to 20 seconds and multiple resolutions',
    model: 'fal-ai/ltxv-2/image-to-video/fast',
    isNew: false,
    isBest: true,
    credits: 20, // Default display (10s × 2)
    needsEndpointSuffix: false,
    icon: LTXVSvg,
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 2,
      duration: ['10', '6', '8', '12', '14', '16', '18', '20'],
      aspectRatio: ['16:9'],
      inputType: ['image'],
      generatesAudio: true,
    } as VideoModelOptions,
  },
  // {
  //   id: 'veo3',
  //   title: 'Google Veo 3',
  //   description:
  //     'Latest Google Veo model with enhanced video generation capabilities and ultra-high quality output',
  //   model: 'veo-3.0-generate-001',
  //   isNew: false,
  //   isBest: true,
  //   credits: 45,
  //   needsEndpointSuffix: false,
  //   icon: GoogleSvg,
  //   options: {
  //     duration: ['8'],
  //     aspectRatio: ['16:9', '9:16'],
  //     inputType: ['text'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'veo3-fast',
  //   title: 'Google Veo 3 Fast',
  //   description:
  //     'Fast version of Google Veo 3 with quick generation times while maintaining high quality output',
  //   model: 'veo-3.0-fast-generate-001',
  //   isNew: false,
  //   credits: 25,
  //   needsEndpointSuffix: false,
  //   icon: GoogleSvg,
  //   options: {
  //     duration: ['8'],
  //     aspectRatio: ['16:9', '9:16'],
  //     inputType: ['text'],
  //   } as VideoModelOptions,
  // },
  // Sora 2 / Sora 2 Pro removed 2026-07-23 — fal endpoint deprecated
  // ("This endpoint is deprecated. This model is no longer supported.")
  // {
  //   id: 'sora-2-text',
  //   title: 'Sora 2',
  //   description:
  //     'OpenAI Sora 2 text-to-video model with cinematic quality and realistic motion',
  //   model: 'fal-ai/sora-2/text-to-video',
  //   isNew: false,
  //   isBest: true,
  //   credits: 20, // Default display (4s × 5)
  //   needsEndpointSuffix: false,
  //   icon: OpenAISvg,
  //   options: {
  //     maxPromptLength: 5000,
  //     creditsPerSecond: 5,
  //     duration: ['4', '8', '12'],
  //     aspectRatio: ['16:9', '9:16'],
  //     inputType: ['text'],
  //     generatesAudio: true,
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'sora-2-pro-text',
  //   title: 'Sora 2 Pro',
  //   description:
  //     'OpenAI Sora 2 Pro text-to-video model with premium quality and advanced features',
  //   model: 'fal-ai/sora-2/text-to-video/pro',
  //   isNew: false,
  //   isBest: true,
  //   credits: 60, // Default display (4s × 15)
  //   needsEndpointSuffix: false,
  //   icon: OpenAISvg,
  //   options: {
  //     maxPromptLength: 5000,
  //     creditsPerSecond: 15,
  //     duration: ['4', '8', '12'],
  //     aspectRatio: ['16:9', '9:16'],
  //     inputType: ['text'],
  //     generatesAudio: true,
  //   } as VideoModelOptions,
  // },
  {
    id: 'hailuo-02-text',
    title: 'Hailuo 2.0',
    description:
      'Advanced text-to-video generation with improved quality and motion dynamics',
    model: 'fal-ai/minimax/hailuo-02/standard/text-to-video',
    isNew: false,
    isBest: true,
    credits: 15,
    needsEndpointSuffix: false,
    icon: HailuoSvg,
    options: {
      duration: ['6', '10'],
      aspectRatio: ['16:9'],
      inputType: ['text'],
    } as VideoModelOptions,
  },
  {
    id: 'hailuo-02-pro-text',
    title: 'Hailuo 2.0 Pro',
    description:
      'Premium text-to-video generation with superior quality and enhanced motion dynamics',
    model: 'fal-ai/minimax/hailuo-02/pro/text-to-video',
    isNew: false,
    isBest: true,
    credits: 25,
    needsEndpointSuffix: false,
    icon: HailuoSvg,
    options: {
      aspectRatio: ['16:9'],
      inputType: ['text'],
    } as VideoModelOptions,
  },
  {
    id: 'hailuo-23-text',
    title: 'Hailuo 2.3',
    description:
      'New breakthroughs in body movement, facial expressions, and physical realism with advanced quality',
    model: 'fal-ai/minimax/hailuo-2.3/standard/text-to-video',
    isNew: false,
    isBest: true,
    credits: 14,
    needsEndpointSuffix: false,
    icon: HailuoSvg,
    options: {
      duration: ['6', '10'],
      aspectRatio: ['16:9'],
      inputType: ['text'],
    } as VideoModelOptions,
  },
  {
    id: 'hailuo-23-pro-text',
    title: 'Hailuo 2.3 Pro',
    description:
      'Premium quality with exceptional body movement, facial expressions, and physical realism',
    model: 'fal-ai/minimax/hailuo-2.3/pro/text-to-video',
    isNew: false,
    isBest: true,
    credits: 24,
    needsEndpointSuffix: false,
    icon: HailuoSvg,
    options: {
      duration: ['5'],
      aspectRatio: ['16:9'],
      inputType: ['text'],
    } as VideoModelOptions,
  },
  {
    id: 'hailuo-23-image',
    title: 'Hailuo 2.3 Image',
    description:
      'Transform images with breakthroughs in body movement, facial expressions, and physical realism',
    model: 'fal-ai/minimax/hailuo-2.3/standard/image-to-video',
    isNew: false,
    isBest: true,
    credits: 14,
    needsEndpointSuffix: false,
    icon: HailuoSvg,
    options: {
      duration: ['6', '10'],
      aspectRatio: ['16:9'],
      inputType: ['image'],
    } as VideoModelOptions,
  },
  {
    id: 'hailuo-23-pro-image',
    title: 'Hailuo 2.3 Pro Image',
    description:
      'Premium image-to-video with exceptional quality, body movement, and facial expressions',
    model: 'fal-ai/minimax/hailuo-2.3/pro/image-to-video',
    isNew: false,
    isBest: true,
    credits: 24,
    needsEndpointSuffix: false,
    icon: HailuoSvg,
    options: {
      duration: ['5'],
      aspectRatio: ['16:9'],
      inputType: ['image'],
    } as VideoModelOptions,
  },
  {
    id: 'hailuo-23-fast-pro-image',
    title: 'Hailuo 2.3-Fast Pro Image',
    description:
      'Fast and efficient premium image-to-video with excellent value and quality',
    model: 'fal-ai/minimax/hailuo-2.3-fast/pro/image-to-video',
    isNew: false,
    isBest: true,
    credits: 16,
    needsEndpointSuffix: false,
    icon: HailuoSvg,
    options: {
      duration: ['6'],
      aspectRatio: ['16:9'],
      inputType: ['image'],
    } as VideoModelOptions,
  },

  // {
  //   id: 'seedance-v1-text',
  //   title: 'Seedance v1',
  //   description:
  //     "Generate videos from text prompts using ByteDance's advanced Seedance v1 model",
  //   model: 'fal-ai/bytedance/seedance/v1/lite/text-to-video',
  //   isNew: false,
  //   isBest: true,
  //   credits: 12,
  //   needsEndpointSuffix: false,
  //   icon: ByteDanceSvg,
  //   options: {
  //     duration: ['5', '10'],
  //     aspectRatio: ['16:9', '4:3', '1:1', '9:21'],
  //     inputType: ['text'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'seedance-v1-pro-text',
  //   title: 'Seedance v1 Pro',
  //   description:
  //     "Premium text-to-video generation with ByteDance's advanced Seedance Pro technology for exceptional quality",
  //   model: 'fal-ai/bytedance/seedance/v1/pro/text-to-video',
  //   isNew: false,
  //   isBest: true,
  //   credits: 45,
  //   needsEndpointSuffix: false,
  //   icon: ByteDanceSvg,
  //   options: {
  //     duration: ['5', '10'],
  //     aspectRatio: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16'],
  //     inputType: ['text'],
  //   } as VideoModelOptions,
  // },
  {
    id: 'seedance-v1-pro-fast-text',
    title: 'Seedance v1 Pro Fast',
    description:
      "Fast premium text-to-video generation with ByteDance's Seedance Pro technology for quick high-quality results",
    model: 'fal-ai/bytedance/seedance/v1/pro/fast/text-to-video',
    isNew: false,
    isBest: true,
    credits: 8, // Default display (5s × 1.5, rounded up)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 1.5,
      duration: ['5', '6', '7', '8', '9', '10', '11', '12', '3', '4'],
      aspectRatio: ['1:1', '3:4', '9:16', '16:9', '4:3', '21:9'],
      inputType: ['text'],
    } as VideoModelOptions,
  },
  {
    id: 'pixverse-v55-text',
    title: 'Pixverse v5.5',
    description:
      'Generate high quality video clips from text prompts with improved visual fidelity',
    model: 'fal-ai/pixverse/v5.5/text-to-video',
    isNew: false,
    isBest: true,
    credits: 15,
    needsEndpointSuffix: false,
    icon: PixverseSvg,
    options: {
      maxPromptLength: 5000,
      duration: ['5', '10'],
      aspectRatio: ['16:9', '4:3', '1:1', '3:4', '9:16'],
      inputType: ['text'],
      generatesAudio: true,
      additionalParams: { generate_audio_switch: true },
    } as VideoModelOptions,
  },
  // {
  //   id: 'pixverse-v5-text',
  //   title: 'Pixverse v5',
  //   description:
  //     'Advanced text-to-video generation with high-quality output and flexible aspect ratios',
  //   model: 'fal-ai/pixverse/v5/text-to-video',
  //   isNew: false,
  //   isBest: false,
  //   credits: 50,
  //   needsEndpointSuffix: false,
  //   icon: PixverseSvg,
  //   options: {
  //     duration: ['5', '8'],
  //     aspectRatio: ['16:9', '4:3', '1:1', '3:4', '9:16'],
  //     inputType: ['text'],
  //   } as VideoModelOptions,
  // },
  {
    id: 'wan-25-preview-text',
    title: 'Wan 2.5',
    description:
      'Latest Wan 2.5 preview model for high-quality text-to-video generation with enhanced motion dynamics',
    model: 'fal-ai/wan-25-preview/text-to-video',
    isNew: false,
    isBest: true,
    credits: 35,
    needsEndpointSuffix: false,
    icon: WanSvg,
    options: {
      maxPromptLength: 1500,
      generatesAudio: true,
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['text'],
    } as VideoModelOptions,
  },
  {
    id: 'wan-v22-a14b-turbo-text',
    title: 'Wan v2.2 Turbo',
    description:
      'Ultra-fast text-to-video generation with enhanced quality and improved motion dynamics',
    model: 'fal-ai/wan/v2.2-a14b/text-to-video/turbo',
    isNew: false,
    isBest: true,
    credits: 5,
    needsEndpointSuffix: false,
    icon: WanSvg,
    options: {
      aspectRatio: ['16:9', '9:16'],
      inputType: ['text'],
    } as VideoModelOptions,
  },
  {
    id: 'wan-v22-a14b-text',
    title: 'Wan v2.2',
    description:
      'Advanced text-to-video generation with enhanced quality and improved motion dynamics',
    model: 'fal-ai/wan/v2.2-a14b/text-to-video',
    isNew: false,
    isBest: true,
    credits: 20,
    needsEndpointSuffix: false,
    icon: WanSvg,
    options: {
      aspectRatio: ['16:9', '9:16'],
      inputType: ['text'],
    } as VideoModelOptions,
  },
  // {
  //   id: 'wan-t2v',
  //   title: 'Wan-2.1',
  //   description:
  //     'Generates high-quality videos with high visual quality and motion diversity from text prompts',
  //   model: 'fal-ai/wan-t2v',
  //   credits: 20,
  //   needsEndpointSuffix: false,
  //   icon: WanSvg,
  //   options: {
  //     aspectRatio: ['16:9', '9:16'],
  //     inputType: ['text'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'kling-pro-v21',
  //   title: 'Kling 2.1 Pro',
  //   description: 'Latest version with improved generation',
  //   model: 'fal-ai/kling-video/v2.1/pro',
  //   isNew: false,
  //   isBest: true,
  //   credits: 23,
  //   needsEndpointSuffix: true,
  //   icon: KlingSvg,
  //   options: {
  //     duration: ['5', '10'],
  //     aspectRatio: ['16:9', '9:16', '1:1'],
  //     inputType: ['image'],
  //     supportsTailImage: true,
  //     tailImageFieldName: 'tail_image_url',
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'veo3-fast-image',
  //   title: 'Google Veo 3.0 Fast',
  //   description:
  //     'Fast version of Google Veo 3 with quick generation times while maintaining high quality output',
  //   model: 'veo-3.0-fast-generate-preview',
  //   isNew: false,
  //   isBest: true,
  //   credits: 25,
  //   needsEndpointSuffix: false,
  //   icon: GoogleSvg,
  //   options: {
  //     duration: ['8'],
  //     aspectRatio: ['16:9', '9:16'],
  //     inputType: ['image'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'veo3-image',
  //   title: 'Google Veo 3.0',
  //   description:
  //     'Latest Google Veo model with enhanced video generation capabilities and ultra-high quality output',
  //   model: 'veo-3.0-generate-preview',
  //   isNew: false,
  //   isBest: true,
  //   credits: 45,
  //   needsEndpointSuffix: false,
  //   icon: GoogleSvg,
  //   options: {
  //     duration: ['8'],
  //     aspectRatio: ['16:9', '9:16'],
  //     inputType: ['image'],
  //   } as VideoModelOptions,
  // },
  // Sora 2 Image / Sora 2 Pro Image removed 2026-07-23 — fal endpoint deprecated
  // {
  //   id: 'sora-2-image',
  //   title: 'Sora 2 Image',
  //   description:
  //     'OpenAI Sora 2 image-to-video model with exceptional motion control and quality',
  //   model: 'fal-ai/sora-2/image-to-video',
  //   isNew: false,
  //   isBest: true,
  //   credits: 20, // Default display (4s × 5)
  //   needsEndpointSuffix: false,
  //   icon: OpenAISvg,
  //   options: {
  //     maxPromptLength: 5000,
  //     creditsPerSecond: 5,
  //     duration: ['4', '8', '12'],
  //     aspectRatio: ['16:9', '9:16'],
  //     inputType: ['image'],
  //     generatesAudio: true,
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'sora-2-pro-image',
  //   title: 'Sora 2 Pro Image',
  //   description:
  //     'OpenAI Sora 2 Pro image-to-video model with premium quality and superior motion dynamics',
  //   model: 'fal-ai/sora-2/image-to-video/pro',
  //   isNew: false,
  //   isBest: true,
  //   credits: 60, // Default display (4s × 15)
  //   needsEndpointSuffix: false,
  //   icon: OpenAISvg,
  //   options: {
  //     maxPromptLength: 5000,
  //     creditsPerSecond: 15,
  //     duration: ['4', '8', '12'],
  //     aspectRatio: ['16:9', '9:16'],
  //     inputType: ['image'],
  //     generatesAudio: true,
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'kling-v21',
  //   title: 'Kling 2.1',
  //   description: 'Latest version with improved generation',
  //   model: 'fal-ai/kling-video/v2.1/standard',
  //   isNew: false,
  //   credits: 13,
  //   needsEndpointSuffix: true,
  //   icon: KlingSvg,
  //   options: {
  //     duration: ['5', '10'],
  //     aspectRatio: ['16:9', '9:16', '1:1'],
  //     inputType: ['image'],
  //   } as VideoModelOptions,
  // },
  {
    id: 'hailuo-02-image',
    title: 'Hailuo 2.0 Image',
    description:
      'Transform images into high-quality videos with enhanced motion and detail preservation',
    model: 'fal-ai/minimax/hailuo-02/standard/image-to-video',
    isNew: false,
    credits: 15,
    needsEndpointSuffix: false,
    icon: HailuoSvg,
    options: {
      duration: ['6', '10'],
      aspectRatio: ['16:9'],
      inputType: ['image'],
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
    } as VideoModelOptions,
  },
  {
    id: 'hailuo-02-pro-image',
    title: 'Hailuo 2.0 Pro Image',
    description:
      'Premium image-to-video transformation with exceptional quality and motion fidelity',
    model: 'fal-ai/minimax/hailuo-02/pro/image-to-video',
    isNew: false,
    isBest: true,
    credits: 25,
    needsEndpointSuffix: false,
    icon: HailuoSvg,
    options: {
      aspectRatio: ['16:9'],
      inputType: ['image'],
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
    } as VideoModelOptions,
  },
  // {
  //   id: 'seedance-v1-image',
  //   title: 'Seedance 1.0',
  //   description:
  //     "Transform images into dynamic videos with ByteDance's Seedance technology",
  //   model: 'fal-ai/bytedance/seedance/v1/lite/image-to-video',
  //   isNew: false,
  //   credits: 12,
  //   needsEndpointSuffix: false,
  //   icon: ByteDanceSvg,
  //   options: {
  //     duration: ['5', '10'],
  //     aspectRatio: ['16:9'],
  //     inputType: ['image'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'seedance-1-pro',
  //   title: 'Seedance 1.0 Pro',
  //   description:
  //     "Premium image-to-video transformation with ByteDance's advanced Seedance Pro technology for superior quality",
  //   model: 'fal-ai/bytedance/seedance/v1/pro/image-to-video',
  //   isNew: false,
  //   isBest: true,
  //   credits: 45,
  //   needsEndpointSuffix: false,
  //   icon: ByteDanceSvg,
  //   options: {
  //     duration: ['5', '10'],
  //     aspectRatio: ['16:9'],
  //     inputType: ['image'],
  //   } as VideoModelOptions,
  // },
  {
    id: 'seedance-1-pro-fast',
    title: 'Seedance 1.0 Pro Fast',
    description:
      "Fast premium image-to-video transformation with ByteDance's Seedance Pro technology for rapid high-quality results",
    model: 'fal-ai/bytedance/seedance/v1/pro/fast/image-to-video',
    isNew: true,
    isBest: true,
    credits: 8, // Default display (5s × 1.5, rounded up)
    needsEndpointSuffix: false,
    icon: ByteDanceSvg,
    options: {
      maxPromptLength: 5000,
      creditsPerSecond: 1.5,
      duration: ['5', '6', '7', '8', '9', '10', '11', '12', '3', '4'],
      aspectRatio: ['1:1', '3:4', '9:16', '16:9', '4:3', '21:9'],
      inputType: ['image'],
    } as VideoModelOptions,
  },

  // {
  //   id: 'veo3-image',
  //   title: 'Google Veo 3',
  //   description:
  //     "Transform images into ultra-high quality videos with Google's latest Veo 3 model",
  //   model: 'veo-3.0-generate-preview',
  //   isNew: true,
  //   isBest: true,
  //   credits: 90,
  //   needsEndpointSuffix: false,
  //   options: {
  //     aspectRatio: ['16:9', '9:16'],
  //     inputType: ['image'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'kling-v21-master-image',
  //   title: 'Kling 2.1 Master',
  //   description:
  //     'Latest master version for image-to-video generation with superior quality',
  //   model: 'fal-ai/kling-video/v2.1/master/image-to-video',
  //   isNew: false,
  //   isBest: true,
  //   credits: 70,
  //   needsEndpointSuffix: false,
  //   icon: KlingSvg,
  //   options: {
  //     duration: ['5', '10'],
  //     aspectRatio: ['16:9', '9:16', '1:1'],
  //     inputType: ['image'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'kling-v21-master-text',
  //   title: 'Kling 2.1 Master',
  //   description:
  //     'Latest master version for text-to-video generation with superior quality',
  //   model: 'fal-ai/kling-video/v2.1/master/text-to-video',
  //   isNew: false,
  //   isBest: true,
  //   credits: 70,
  //   needsEndpointSuffix: false,
  //   icon: KlingSvg,
  //   options: {
  //     duration: ['5', '10'],
  //     aspectRatio: ['16:9', '9:16', '1:1'],
  //     inputType: ['text'],
  //   } as VideoModelOptions,
  // },
  {
    id: 'pixverse-v55-image',
    title: 'Pixverse v5.5 Image',
    description:
      'Generate high quality video clips from image prompts with improved visual fidelity',
    model: 'fal-ai/pixverse/v5.5/image-to-video',
    isNew: false,
    isBest: true,
    credits: 15,
    needsEndpointSuffix: false,
    icon: PixverseSvg,
    options: {
      maxPromptLength: 5000,
      duration: ['5', '10'],
      aspectRatio: ['16:9', '4:3', '1:1', '3:4', '9:16'],
      inputType: ['image'],
      generatesAudio: true,
      additionalParams: { generate_audio_switch: true },
    } as VideoModelOptions,
  },
  // {
  //   id: 'pixverse-v5-image',
  //   title: 'Pixverse v5 Image',
  //   description:
  //     'Transform images into dynamic videos with advanced motion control and multiple aspect ratios',
  //   model: 'fal-ai/pixverse/v5/image-to-video',
  //   isNew: false,
  //   isBest: false,
  //   credits: 50,
  //   needsEndpointSuffix: false,
  //   icon: PixverseSvg,
  //   options: {
  //     duration: ['5', '8'],
  //     aspectRatio: ['16:9', '4:3', '1:1', '3:4', '9:16'],
  //     inputType: ['image'],
  //   } as VideoModelOptions,
  // },
  {
    id: 'wan-25-preview-image',
    title: 'Wan 2.5 Image',
    description:
      'Latest Wan 2.5 preview model for high-quality image-to-video transformation with superior motion control',
    model: 'fal-ai/wan-25-preview/image-to-video',
    isNew: true,
    isBest: true,
    credits: 35,
    needsEndpointSuffix: false,
    icon: WanSvg,
    generatesAudio: true,
    options: {
      maxPromptLength: 1500,
      aspectRatio: ['16:9', '9:16', '1:1'],
      inputType: ['image'],
    } as VideoModelOptions,
  },
  {
    id: 'wan-v22-a14b-turbo-image',
    title: 'Wan v2.2 Turbo Image',
    description:
      'Ultra-fast image-to-video generation with enhanced quality and improved motion dynamics',
    model: 'fal-ai/wan/v2.2-a14b/image-to-video/turbo',
    isNew: false,
    isBest: true,
    credits: 5,
    needsEndpointSuffix: false,
    icon: WanSvg,
    options: {
      aspectRatio: ['16:9', '9:16'],
      inputType: ['image'],
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
    } as VideoModelOptions,
  },
  {
    id: 'wan-i2v',
    title: 'Wan-2.2 Image',
    description:
      'Transform images into high-quality videos with high visual quality and motion diversity',
    model: 'fal-ai/wan/v2.2-a14b/image-to-video',
    credits: 20,
    needsEndpointSuffix: false,
    icon: WanSvg,
    options: {
      aspectRatio: ['1:1', '16:9', '9:16'],
      inputType: ['image'],
      supportsTailImage: true,
      tailImageFieldName: 'end_image_url',
    } as VideoModelOptions,
  },
  // {
  //   id: 'veo2-image',
  //   title: 'Google Veo 2',
  //   description:
  //     'Transform images into high-quality videos with realistic motion and extensive camera controls, up to 4K resolution',
  //   model: 'veo-2.0-generate-001',
  //   credits: 90,
  //   needsEndpointSuffix: false,
  //   icon: GoogleSvg,
  //   options: {
  //     aspectRatio: ['16:9', '9:16'],
  //     inputType: ['image'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'hailuo',
  //   title: 'MiniMax Video-01-Live',
  //   description: 'Specialized in smooth transitions and scene animations',
  //   model: 'fal-ai/minimax/video-01-live',
  //   credits: 18,
  //   needsEndpointSuffix: true,
  //   icon: ByteDanceSvg,
  //   options: {
  //     ...defaultOptions,
  //     inputType: ['image'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'haiper-25-image',
  //   title: 'Haiper v2.5',
  //   description:
  //     'Transform images into hyper-realistic videos with industry-leading resolution and fluid motion',
  //   model: 'fal-ai/haiper-video/v2.5/image-to-video/fast',
  //   credits: 15,
  //   needsEndpointSuffix: false,
  //   icon: AnthropicSvg,
  //   options: {
  //     duration: ['4', '6'],
  //     aspectRatio: ['16:9'],
  //     inputType: ['image'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'kling-v16',
  //   title: 'Kling 1.6',
  //   description:
  //     'Latest standard version with improved generation capabilities',
  //   model: 'fal-ai/kling-video/v1.6/standard',
  //   isNew: true,
  //   credits: 8,
  //   needsEndpointSuffix: true,
  //   options: {
  //     duration: ['5', '10'],
  //     aspectRatio: ['16:9', '9:16', '1:1'],
  //     inputType: ['image', 'text'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'veo2',
  //   title: 'Google Veo 2.0',
  //   description:
  //     'Creates videos with realistic motion and high quality output, up to 4K resolution with extensive camera controls',
  //   model: 'veo-2.0-generate-001',
  //   credits: 100,
  //   needsEndpointSuffix: false,
  //   icon: GoogleSvg,
  //   options: {
  //     aspectRatio: ['16:9', '9:16'],
  //     inputType: ['text'],
  //   } as VideoModelOptions,
  // },

  // {
  //   id: 'haiper-25-text',
  //   title: 'Haiper 2.5',
  //   description:
  //     'Transform text into hyper-realistic videos with industry-leading resolution and fluid motion',
  //   model: 'fal-ai/haiper-video/v2.5/fast',
  //   credits: 15,
  //   needsEndpointSuffix: false,
  //   icon: AnthropicSvg,
  //   options: {
  //     duration: ['4', '6'],
  //     aspectRatio: ['16:9'],
  //     inputType: ['text'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'video-01-live',
  //   title: 'MiniMax Video-01 Live',
  //   description: 'Specialized in smooth transitions and scene animations',
  //   model: 'fal-ai/minimax/video-01-live',
  //   credits: 18,
  //   needsEndpointSuffix: false,
  //   icon: ByteDanceSvg,
  //   options: {
  //     aspectRatio: ['16:9'],
  //     inputType: ['text'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'luma',
  //   title: 'Luma Dream Machine',
  //   description: 'Best for realistic motion and high-quality video generation',
  //   model: 'fal-ai/luma-dream-machine',
  //   credits: 18,
  //   needsEndpointSuffix: true,
  //   options: {
  //     aspectRatio: ['16:9', '9:16', '4:3', '3:4', '21:9', '9:21'],
  //     inputType: ['image', 'text'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'hunyuan',
  //   title: 'Hunyuan Video',
  //   description:
  //     'Advanced text-to-video generation with detailed scene control',
  //   model: 'fal-ai/hunyuan-video',
  //   credits: 15,
  //   needsEndpointSuffix: false,
  //   icon: ByteDanceSvg,
  //   options: {
  //     aspectRatio: ['16:9'],
  //     inputType: ['text'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'runway',
  //   title: 'Runway Gen3 Alpha',
  //   description: 'Excellent for creative and artistic video transformations',
  //   model: 'fal-ai/runway-gen3/turbo',
  //   isNew: true,
  //   credits: 10,
  //   needsEndpointSuffix: true,
  //   options: {
  //     duration: ['5'],
  //     aspectRatio: ['16:9', '9:16'],
  //     inputType: ['image'],
  //   } as VideoModelOptions,
  // },

  // {
  //   id: 'kling-pro-v15',
  //   title: 'Kling 1.5 Pro',
  //   description: 'Latest version with advanced quality and enhanced control',
  //   model: 'fal-ai/kling-video/v1.5/pro',
  //   credits: 25,
  //   needsEndpointSuffix: true,
  //   options: {
  //     duration: ['5', '10'],
  //     aspectRatio: ['16:9', '9:16', '1:1'],
  //     inputType: ['image', 'text'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'kling',
  //   title: 'Kling 1.0',
  //   description: 'Great for character animations and stylized videos',
  //   model: 'fal-ai/kling-video/v1/standard',
  //   credits: 8,
  //   needsEndpointSuffix: true,
  //   options: {
  //     duration: ['5', '10'],
  //     aspectRatio: ['16:9', '9:16', '1:1'],
  //     inputType: ['image', 'text'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'kling-pro',
  //   title: 'Kling 1.0 Pro',
  //   description: 'Enhanced version with better quality and control',
  //   model: 'fal-ai/kling-video/v1/pro',
  //   isBest: true,
  //   credits: 30,
  //   needsEndpointSuffix: true,
  //   options: {
  //     duration: ['5', '10'],
  //     aspectRatio: ['16:9', '9:16', '1:1'],
  //     inputType: ['image', 'text'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'luma-ray2-image',
  //   title: 'Luma Ray-2 Image',
  //   description:
  //     'Transform images into cinematic videos with advanced motion and high fidelity',
  //   model: 'fal-ai/luma-dream-machine/ray-2/image-to-video',
  //   credits: 20,
  //   needsEndpointSuffix: false,
  //   icon: AnthropicSvg,
  //   options: {
  //     duration: ['5s'],
  //     aspectRatio: ['16:9', '9:16', '4:3', '3:4', '21:9', '9:21'],
  //     inputType: ['image'],
  //   } as VideoModelOptions,
  // },
  // {
  //   id: 'luma-ray2-text',
  //   title: 'Luma Ray-2',
  //   description:
  //     'Create cinematic videos from text with stunning visual quality and dynamic motion',
  //   model: 'fal-ai/luma-dream-machine/ray-2',
  //   credits: 20,
  //   needsEndpointSuffix: false,
  //   icon: AnthropicSvg,
  //   options: {
  //     duration: ['5s'],
  //     aspectRatio: ['16:9', '9:16', '4:3', '3:4', '21:9', '9:21'],
  //     inputType: ['text'],
  //   } as VideoModelOptions,
  // },
];

export interface VideoModel {
  id: string;
  title: string;
  description: string;
  model: string;
  isNew?: boolean;
  isBest?: boolean;
  /** Renders markedly faster than the rest of the catalogue — badged in the picker. */
  isFast?: boolean;
  /** Billed at a provider launch discount right now — badged in the picker. */
  isPromo?: boolean;
  credits: number;
  needsEndpointSuffix: boolean;
  options: VideoModelOptions;
  icon?: FC<React.SVGProps<SVGSVGElement>>;
  /** Whether this model is available for free users */
  freeUserAccess?: boolean;
  /**
   * Free-plan constraints for freeUserAccess models, enforced in
   * /api/generate-video and mirrored by the UI option pickers.
   * - resolutions / maxDurationSeconds: cap the offered options; free users
   *   pay the standard credit prices within those caps.
   */
  freePlanPolicy?: {
    resolutions?: ResolutionType[];
    maxDurationSeconds?: number;
  };
}

export type VideoModelType =
  (typeof videoModelsWithDescription)[number]['title'];

// Helper functions for workflow nodes
export function getVideoModelsByInputType(inputType: InputType): VideoModel[] {
  return videoModelsWithDescription.filter((model) =>
    model.options.inputType.includes(inputType),
  );
}

export function getAspectRatiosForVideoModel(
  modelTitle: string,
): AspectRatioType[] {
  const model = videoModelsWithDescription.find((m) => m.title === modelTitle);
  return model?.options.aspectRatio || [];
}

export function getDurationsForVideoModel(modelTitle: string): DurationType[] {
  const model = videoModelsWithDescription.find((m) => m.title === modelTitle);
  return model?.options.duration || [];
}

export const DEFAULT_VIDEO_PROMPT_MAX_LENGTH = 2000;

/**
 * Credits per second of INPUT video for Gemini Omni Flash video editing.
 * The model's output duration mirrors the input duration, so an edit is
 * charged per second at the same rate as a generation rather than the flat
 * price. Pinned to the generation rate so the two can never drift.
 * Shared by the /ai-videos credit display and the /api/generate-video charge.
 */
export const OMNI_VIDEO_EDIT_CREDITS_PER_SECOND =
  GEMINI_OMNI_11_CREDITS_PER_SECOND;

/**
 * Credits for a Gemini Omni Flash video edit, given the input video's
 * duration. Rounds up per second with a 100ms grace so container rounding
 * (e.g. the model's own ~10.005s outputs) doesn't tip into the next second.
 * Billed seconds are capped at 10: the model truncates BOTH input ingestion
 * and output length at ~10s (verified: a 15s input consumed exactly 10s of
 * input tokens and rendered a 10s output), so seconds past 10 cost Google
 * nothing and shouldn't cost the user either.
 */
export function getOmniVideoEditCredits(
  durationSeconds: number,
  resolution?: ResolutionType,
): number {
  const billedSeconds = Math.min(
    Math.max(1, Math.ceil(durationSeconds - 0.1)),
    10,
  );
  const cost = billedSeconds * OMNI_VIDEO_EDIT_CREDITS_PER_SECOND;
  // Omni 1.1 renders edits at the chosen tier, and Google prices those tiers
  // the same way it prices generation — so an edit scales with resolution too.
  // The preview entry has no resolution control and passes nothing here, so it
  // keeps its flat rate.
  const multiplier =
    resolution && GEMINI_OMNI_11_RESOLUTION_MULTIPLIER[resolution];
  return multiplier ? Math.ceil(cost * multiplier) : cost;
}

/**
 * Whether a catalogue model runs on the Gemini Omni interactions API — the
 * 1.1 entries and the older preview entries alike.
 *
 * Every Omni entry must be routed by this helper rather than an equality check
 * against a single model id: /api/generate-video, the poll cron and the video
 * dispatcher each branch on it, and a new Omni version added to the catalogue
 * without updating a hard-coded id would silently fall through to the generic
 * (fal/WaveSpeed) path.
 */
export function isGeminiOmniVideoModel(modelPath?: string | null): boolean {
  return !!modelPath && modelPath.startsWith('gemini-omni-');
}

/**
 * Model title stored on rows produced by the Seedance 2.5 timestamp editor.
 * It is deliberately NOT a catalogue entry: editing is its own endpoint
 * (/api/edit-video) with locked framing and duration, so it must never appear
 * in the Video Generator's model picker.
 */
export const SEEDANCE_25_EDIT_MODEL_TITLE = 'Seedance 2.5 Edit';

/** ModelArk requires the video being edited to be 4-30 seconds long. */
export const SEEDANCE_25_EDIT_MIN_SECONDS = 4;
export const SEEDANCE_25_EDIT_MAX_SECONDS = 30;
/**
 * Upload ceiling for a clip brought into the editor from outside the gallery
 * (the /ai-videos?tab=edit-video tool). Same cap as the Extend tool — both
 * providers fetch the clip by URL, and 100 MB already covers a 30 s 4K
 * phone recording.
 */
export const SEEDANCE_25_EDIT_MAX_SOURCE_BYTES = 100 * 1024 * 1024;

/**
 * Credits per second of SOURCE video for a Seedance 2.5 edit.
 *
 * Both providers bill the input duration as well as the output, and the output
 * length is locked to the input:
 *
 *   ModelArk  `2 × seconds × W × H × 24 / 1024` at $6.40/M
 *               720p $0.2794/s   480p $0.1279/s
 *   WaveSpeed `110_000 × resMult × (inSeconds + outSeconds)` micro-dollars
 *               720p $0.44/s     480p $0.22/s
 *
 * Priced on WaveSpeed (the worst case) like generation, and sold below cost at
 * the same ~2/3 loss-leader rate: cost recovery would be 22 credits at 720p
 * ($0.44/s) and 11 at 480p ($0.22/s). A 30s 720p edit loses ~$4.20 — the
 * largest single-job exposure in the family.
 */
export const SEEDANCE_25_EDIT_CREDITS_PER_SECOND: Record<string, number> = {
  '720p': 15,
  '480p': 7,
};

/**
 * The turbo editing tier (`video-edit-turbo`, WaveSpeed-only) at
 * `(110_000 + (1080p ? 30_000 : 20_000)) × (in + out)` micro-dollars — a flat
 * $0.26/s of source at 720p and $0.28/s at 1080p, i.e. roughly half the
 * standard price. Cost recovery would be 13 and 14 credits per source-second;
 * held at 9/10 for the same ~2/3 loss-leader posture.
 * Renders 720p/1080p only; there is no 480p turbo.
 */
export const SEEDANCE_25_EDIT_TURBO_CREDITS_PER_SECOND: Record<string, number> =
  {
    '720p': 9,
    '1080p': 10,
  };

/**
 * Engines behind the timestamp editor. `standard` and `turbo` are Seedance
 * 2.5 (ModelArk-first with the WaveSpeed fallback, and WaveSpeed's turbo
 * endpoint); `wan-3.0` is Alibaba's WAN 3.0 `video-edit` on WaveSpeed — a
 * different model family and moderation stack, which is what makes it worth
 * offering: a clip Seedance refuses on content grounds usually renders there.
 * `kling-4.0` is Kuaishou's Kling 4.0 `video-edit` on WaveSpeed, a third
 * family that keeps the source's own sound.
 */
export type Seedance25EditTier = 'standard' | 'turbo' | 'wan-3.0' | 'kling-4.0';
export type VideoEditTier = Seedance25EditTier;

/** Model title stored on rows produced by the Kling 4.0 editing engine. */
export const KLING_40_EDIT_MODEL_TITLE = 'Kling 4.0 Edit';

/**
 * WaveSpeed's `kwaivgi/kling-v4.0/video-edit` takes 3-30s of source ("longer
 * videos are limited to the first 30 seconds" — refused here instead of
 * silently cut) and renders the same length back when no duration is sent.
 */
export const KLING_40_EDIT_MIN_SECONDS = 3;
export const KLING_40_EDIT_MAX_SECONDS = 30;

/**
 * Credits per second of SOURCE video for a Kling 4.0 edit, at cost recovery.
 * The formula is `(1080p ? 1.5 : 1) × 94_000 × (output + max(output, source))`
 * micro-dollars with the source ceiled and clamped to [3, 30]; the output
 * follows the source, so that is $0.188 per source-second at 720p and $0.282
 * at 1080p — 9.4 and 14.1 credits, rounded up to whole credits.
 */
export const KLING_40_EDIT_CREDITS_PER_SECOND: Record<string, number> = {
  '720p': 10,
  '1080p': 15,
};

/** Model title stored on rows produced by the WAN 3.0 editing engine. */
export const WAN_30_EDIT_MODEL_TITLE = 'WAN 3.0 Edit';

/**
 * WaveSpeed's `alibaba/wan-3.0/video-edit` takes 2-15s of source ("Videos
 * longer than 15s are trimmed to 15s" — we refuse them instead of silently
 * cutting the tail) and renders the same length back.
 */
export const WAN_30_EDIT_MIN_SECONDS = 2;
export const WAN_30_EDIT_MAX_SECONDS = 15;

/**
 * Credits per second of SOURCE video for a WAN 3.0 edit, at cost recovery
 * like the rest of the WAN 3.0 family. The catalogue formula is
 * `500_000 × resMult × (source + output) / 5` micro-dollars with the output
 * locked to the source length, i.e. $0.20/s @720p, $0.10 @480p, $0.40 @1080p
 * (resMult 0.5 / 1 / 2) — ÷ $0.02 per credit lands exactly on 5 / 10 / 20.
 * The `wan-3.0-prime` sibling is the same formula at 750_000; not wired.
 */
export const WAN_30_EDIT_CREDITS_PER_SECOND: Record<string, number> = {
  '480p': 5,
  '720p': 10,
  '1080p': 20,
};

/** Resolutions each editing engine can render. */
export const SEEDANCE_25_EDIT_RESOLUTIONS: Record<
  Seedance25EditTier,
  ResolutionType[]
> = {
  standard: ['480p', '720p'],
  turbo: ['720p', '1080p'],
  'wan-3.0': ['480p', '720p', '1080p'],
  'kling-4.0': ['720p', '1080p'],
};

/** Source-length window each editing engine accepts, in seconds. */
export const getVideoEditTierLimits = (
  tier: Seedance25EditTier,
): { minSeconds: number; maxSeconds: number } => {
  if (tier === 'wan-3.0') {
    return {
      minSeconds: WAN_30_EDIT_MIN_SECONDS,
      maxSeconds: WAN_30_EDIT_MAX_SECONDS,
    };
  }
  if (tier === 'kling-4.0') {
    return {
      minSeconds: KLING_40_EDIT_MIN_SECONDS,
      maxSeconds: KLING_40_EDIT_MAX_SECONDS,
    };
  }
  return {
    minSeconds: SEEDANCE_25_EDIT_MIN_SECONDS,
    maxSeconds: SEEDANCE_25_EDIT_MAX_SECONDS,
  };
};

/** The model title an editing engine's rows are stored under. */
export const getVideoEditModelTitle = (tier: Seedance25EditTier): string =>
  tier === 'wan-3.0'
    ? WAN_30_EDIT_MODEL_TITLE
    : tier === 'kling-4.0'
      ? KLING_40_EDIT_MODEL_TITLE
      : SEEDANCE_25_EDIT_MODEL_TITLE;

/**
 * Credits for an edit of a clip of the given length on the chosen engine,
 * clamped to that engine's input window (Seedance 2.5 4-30s, WAN 3.0 2-15s,
 * Kling 4.0 3-30s).
 *
 * The seconds are rounded up with NO grace period on purpose: WaveSpeed's edit
 * formulas ceil the source duration for both the input and the output leg
 * (`$ceil(get_duration_v3(video))`), so a clip measuring 5.085s bills as 6 + 6
 * seconds there. Shaving the fraction off here — which is what a grace period
 * does — undercharges by a whole second against the provider we price on.
 */
export function getSeedance25EditCredits(
  durationSeconds: number,
  resolution: ResolutionType = '720p',
  tier: Seedance25EditTier = 'standard',
): number {
  const rates =
    tier === 'wan-3.0'
      ? WAN_30_EDIT_CREDITS_PER_SECOND
      : tier === 'kling-4.0'
        ? KLING_40_EDIT_CREDITS_PER_SECOND
        : tier === 'turbo'
          ? SEEDANCE_25_EDIT_TURBO_CREDITS_PER_SECOND
          : SEEDANCE_25_EDIT_CREDITS_PER_SECOND;
  const perSecond = rates[resolution] ?? rates['720p'];
  const { minSeconds, maxSeconds } = getVideoEditTierLimits(tier);
  const billedSeconds = Math.min(
    Math.max(minSeconds, Math.ceil(Number(durationSeconds) || 0)),
    maxSeconds,
  );
  return billedSeconds * perSecond;
}

/**
 * Extra credits for the reference video clips attached to a generation.
 *
 * Seedance and MiniMax H3 bill a reference clip's own duration on top of the
 * generated output, at the same per-second rate (MiniMax states it directly;
 * ModelArk's Seedance token formula counts input video seconds). So a 4s clip
 * generated against a 15s reference costs the provider almost five times what
 * the output alone would — without this the difference would be pure loss, and
 * it is user-triggerable at will.
 *
 * Mirrors getVideoModelCredits' per-second math (resolution multiplier, then
 * the audio-off discount) so a reference second is priced exactly like an
 * output second. Returns 0 for models that don't bill input duration —
 * including Gemini Omni Flash, whose edits are already priced off the input
 * alone (see getOmniVideoEditCredits).
 *
 * Models with `countedSecondsCreditsPerSecond` (Kling 4.0) are re-priced as a
 * whole once a clip is attached, so for them this returns the difference
 * between that price and the plain output price — which needs the output
 * length (`outputDuration`, the same value passed to getVideoModelCredits).
 */
export function getReferenceVideoCredits(
  modelTitle: string,
  clipDurationsSeconds: number[],
  resolution?: ResolutionType,
  generateAudio?: boolean,
  outputDuration?: string,
): number {
  const model = videoModelsWithDescription.find((m) => m.title === modelTitle);
  const config = model?.options.referenceVideoConfig;
  if (!model || !config) return 0;

  // Whole seconds per clip — providers bill input duration ceiled — and never
  // more than the model's combined cap, which submission also enforces.
  const seconds = Math.min(
    clipDurationsSeconds.reduce(
      (total, clip) => total + Math.max(0, Math.ceil(Number(clip) || 0)),
      0,
    ),
    config.maxTotalSeconds,
  );
  if (seconds <= 0) return 0;

  const applyMultipliers = (credits: number) => {
    let cost = credits;
    const multiplier =
      resolution && model.options.resolutionCreditsMultiplier?.[resolution];
    if (multiplier) cost = Math.ceil(cost * multiplier);
    if (generateAudio === false) {
      cost = Math.ceil(
        cost * (model.options.audioDisabledCreditsMultiplier ?? 1),
      );
    }
    return cost;
  };

  if (config.countedSecondsCreditsPerSecond) {
    const output = parseInt(
      String(outputDuration ?? getDefaultDurationForModel(model) ?? '').replace(
        's',
        '',
      ),
      10,
    );
    if (!Number.isFinite(output) || output <= 0) return 0;
    const withReference = applyMultipliers(
      Math.ceil(
        (output + Math.max(output, seconds)) *
          config.countedSecondsCreditsPerSecond,
      ),
    );
    const withoutReference = getVideoModelCredits(
      modelTitle,
      String(output),
      resolution,
      generateAudio,
    );
    return Math.max(0, withReference - withoutReference);
  }

  const perSecond = model.options.creditsPerSecond;
  if (!config.billsInputSeconds || !perSecond) return 0;

  return applyMultipliers(Math.ceil(seconds * perSecond));
}

/**
 * Reference-input charge for models priced by pooled TOKENS rather than by
 * reference seconds (`referenceTokenPricing` — fal's MiniMax H3 Max reference
 * endpoint). Returns 0 for every other model, so callers can add it
 * unconditionally beside getVideoModelCredits, exactly like
 * getReferenceVideoCredits.
 *
 * `resolution` is the tier the OUTPUT is generated at: it sets the per-second
 * token rate for reference clips, and an omitted value bills at the model's
 * default tier (768p — also fal's default, and the dearer of the two, so an
 * API caller cannot pay the 480p rate for a 768p render).
 *
 * Image dimensions are a billing basis, so server callers MUST pass values
 * measured from the files themselves (readRemoteImageDimensions), never
 * numbers supplied by the client. The Video Generator passes browser-measured
 * ones for its estimate only, and the server re-measures before charging.
 */
export function getReferenceTokenCredits(
  modelTitle: string,
  references: {
    imageDimensions?: { width: number; height: number }[];
    videoDurationsSeconds?: number[];
  },
  resolution?: ResolutionType,
): number {
  const model = videoModelsWithDescription.find((m) => m.title === modelTitle);
  const pricing = model?.options.referenceTokenPricing;
  if (!model || !pricing) return 0;

  let tokens = 0;

  for (const size of references.imageDimensions ?? []) {
    const width = Number(size?.width) || 0;
    const height = Number(size?.height) || 0;
    if (width <= 0 || height <= 0) continue;
    tokens += Math.ceil((width * height) / pricing.imageTokenDivisor);
  }

  const tier = resolution ?? getDefaultResolutionForModel(model);
  const tokensPerSecond = tier ? pricing.videoTokensPerSecond[tier] : undefined;
  if (tokensPerSecond) {
    // Whole seconds per clip, and never more than the model's combined cap —
    // which submission enforces too.
    const seconds = Math.min(
      (references.videoDurationsSeconds ?? []).reduce(
        (total, clip) => total + Math.max(0, Math.ceil(Number(clip) || 0)),
        0,
      ),
      model.options.referenceVideoConfig?.maxTotalSeconds ?? Infinity,
    );
    tokens += Math.ceil(seconds * tokensPerSecond);
  }

  const billable = tokens - pricing.freeTokens;
  if (billable <= 0) return 0;

  // Rounded up to a whole credit, so a small overage costs a whole credit
  // ($0.02) rather than a fraction of one — the same posture as every other
  // price here, and it can only ever round in our favour.
  return Math.ceil((billable / 1000) * pricing.creditsPerThousandTokens);
}

/**
 * Title-keyed wrappers for the two default resolvers, for callers that hold a
 * model title rather than the catalogue entry (the workflow nodes).
 */
export function getDefaultDurationForModelTitle(
  modelTitle?: string,
): DurationType | undefined {
  return getDefaultDurationForModel(
    videoModelsWithDescription.find((m) => m.title === modelTitle),
  );
}

export function getDefaultResolutionForModelTitle(
  modelTitle?: string,
): ResolutionType | undefined {
  const model = videoModelsWithDescription.find((m) => m.title === modelTitle);
  return model ? getDefaultResolutionForModel(model) : undefined;
}

export function getMaxPromptLengthForVideoModel(modelTitle?: string): number {
  const model = videoModelsWithDescription.find((m) => m.title === modelTitle);
  return model?.options.maxPromptLength ?? DEFAULT_VIDEO_PROMPT_MAX_LENGTH;
}

/**
 * Get credit cost for video models
 * Uses creditsPerSecond field if available, otherwise falls back to special cases
 * @param modelTitle - Model title
 * @param duration - Optional duration for models with duration-dependent pricing
 * @param resolution - Optional resolution for models with resolution-dependent pricing
 * @param generateAudio - Optional flag; when false and model supports audio toggle, applies discount
 */
export function getVideoModelCredits(
  modelTitle: string,
  duration?: string,
  resolution?: ResolutionType,
  generateAudio?: boolean,
): number {
  const model = videoModelsWithDescription.find((m) => m.title === modelTitle);
  if (!model) return 10;

  const baseCredits = model.credits;

  // Calculate base cost from duration
  let cost: number;

  if (!duration) {
    cost = baseCredits;
  } else {
    // Parse duration (handle both '5s' and '5' formats)
    const durationSeconds = parseInt(duration.replace('s', ''));

    // 1. Per-second pricing via creditsPerSecond field (preferred)
    if (model.options.creditsPerSecond !== undefined) {
      cost = Math.ceil(durationSeconds * model.options.creditsPerSecond);
    }
    // 2. Motion control models (uses credits as per-second rate)
    else if (model.options.isMotionControl) {
      cost = Math.ceil(durationSeconds * model.credits);
    }
    // 3. Special fixed pricing (few remaining edge cases)
    // Hailuo 2.3 Standard: duration tiers
    else if (
      model.model === 'fal-ai/minimax/hailuo-2.3/standard/text-to-video' ||
      model.model === 'fal-ai/minimax/hailuo-2.3/standard/image-to-video'
    ) {
      cost = durationSeconds === 10 ? 28 : 14;
    }
    // Hailuo 2.3 Pro: fixed 24 credits
    else if (
      model.model === 'fal-ai/minimax/hailuo-2.3/pro/text-to-video' ||
      model.model === 'fal-ai/minimax/hailuo-2.3/pro/image-to-video'
    ) {
      cost = 24;
    }
    // Hailuo 2.3-Fast Pro Image: fixed 16 credits
    else if (
      model.model === 'fal-ai/minimax/hailuo-2.3-fast/pro/image-to-video'
    ) {
      cost = 16;
    }
    // Ray-2 with 9s duration
    else if (model.model.includes('ray-2') && duration === '9s') {
      cost = 40;
    }
    // 4. Default: double credits for duration=10
    else {
      cost =
        model.options.duration?.includes('10') && duration === '10'
          ? baseCredits * 2
          : baseCredits;
    }
  }

  // Apply resolution multiplier if applicable
  const multiplier =
    resolution && model.options.resolutionCreditsMultiplier?.[resolution];
  if (multiplier) {
    cost = Math.ceil(cost * multiplier);
  }

  // Apply audio-disabled discount if applicable
  if (generateAudio === false) {
    const audioMultiplier = model.options.audioDisabledCreditsMultiplier ?? 1;
    cost = Math.ceil(cost * audioMultiplier);
  }

  return cost;
}

/** Helper to get available resolutions for a model */
export function getResolutionsForVideoModel(
  modelTitle: string,
): ResolutionType[] {
  const model = videoModelsWithDescription.find((m) => m.title === modelTitle);
  return model?.options.resolution || [];
}

/**
 * Default resolution to preselect for a model. Returns the first listed
 * option (so the catalogue's array order controls the UI default — Seedance
 * lists 480p first, others typically lead with 720p), with a 720p fallback.
 */
export function getDefaultResolutionForModel(
  model: VideoModel | undefined,
): ResolutionType {
  const preferred = model?.options.defaultResolution;
  if (preferred && model?.options.resolution?.includes(preferred)) {
    return preferred;
  }
  return model?.options.resolution?.[0] ?? '720p';
}

/**
 * Duration to preselect for a model — `defaultDuration` when it names one of
 * the offered values, else the first option, matching the long-standing
 * array-order convention. Callers must use this instead of `duration[0]` so a
 * model can list its ladder in a readable order and still preselect sensibly.
 */
export function getDefaultDurationForModel(
  model: VideoModel | undefined,
): DurationType | undefined {
  const preferred = model?.options.defaultDuration;
  if (preferred && model?.options.duration?.includes(preferred)) {
    return preferred;
  }
  return model?.options.duration?.[0];
}

/** Labels for resolution display */
export const resolutionLabels: Record<ResolutionType, string> = {
  '360p': '360p (Draft)',
  '480p': '480p (SD)',
  '720p': '720p (HD)',
  '768p': '768p (HD)',
  '1080p': '1080p (Full HD)',
  '2k': '2K (Quad HD)',
  '4k': '4K (Ultra HD)',
};

/**
 * Whether a model ALWAYS embeds audio with no way to switch it off — it bakes
 * sound into every render and exposes no audio parameter (MiniMax H3, Gemini
 * Omni Flash 1.1 and Kling 4.0 today).
 *
 * Callers must treat these as "audio is not optional": no audio toggle, no
 * audio-off discount (there is no cheaper mode to bill for), and mute the clip
 * when compositing it under a separate voiceover, or the model's own dialogue
 * and score play underneath the narration.
 */
export function videoModelAlwaysGeneratesAudio(modelTitle?: string): boolean {
  const model = videoModelsWithDescription.find((m) => m.title === modelTitle);
  return model?.options.alwaysGeneratesAudio === true;
}

/**
 * Helper to check if a model supports an audio toggle. Universal EXCEPT for
 * models that always generate audio — offering the control there lets a user
 * pick "audio off" and still get audio.
 */
export function modelSupportsAudioToggle(modelTitle: string): boolean {
  const model = videoModelsWithDescription.find((m) => m.title === modelTitle);
  if (!model) return false;
  return !videoModelAlwaysGeneratesAudio(modelTitle);
}

/**
 * Get video models that can be driven by reference images alone (no `image` or
 * `tailImage` payload). Excludes image/first-last-frame variants that also accept
 * references but require a primary image input — those would 400 in /api/generate-video
 * when called from the References-to-Video workflow node.
 */
export function getVideoModelsWithReferenceSupport(): VideoModel[] {
  return videoModelsWithDescription.filter(
    (model) =>
      model.options.supportsReferenceImages === true &&
      model.options.inputType.includes('text'),
  );
}

/** Helper to get the reference image config for a given model title */
export function getReferenceImageConfigForModel(
  modelTitle: string,
): ReferenceImageConfig | undefined {
  const model = videoModelsWithDescription.find((m) => m.title === modelTitle);
  return model?.options.referenceImageConfig;
}

/**
 * Whether a model can take image references — itself, or through its
 * reference sibling (explicit `referenceSiblingId`, or the path heuristic
 * `.../reference-to-video[-turbo]` also used by the Video Generator's
 * "Add media" flow). Reference audio always REQUIRES an image reference, so
 * audio-capable models that fail this (Seedance 2.0 Turbo — no reference
 * sibling exists) must not be offered for audio, or the user dead-ends on the
 * "add an image reference" requirement.
 */
export function videoModelCanUseReferenceImages(
  model: (typeof videoModelsWithDescription)[number],
): boolean {
  if (model.options.supportsReferenceImages === true) return true;
  const isRefCapable = (m: (typeof videoModelsWithDescription)[number]) =>
    m.options.supportsReferenceImages === true &&
    !!m.options.referenceImageConfig &&
    !m.options.isMotionControl;
  const explicitId = model.options.referenceSiblingId;
  if (
    explicitId &&
    videoModelsWithDescription.some(
      (m) => m.id === explicitId && isRefCapable(m),
    )
  ) {
    return true;
  }
  const path = model.model;
  const base = path.replace(/\/[^/]+$/, '');
  const leaf = path.slice(base.length + 1);
  const turboSuffix = leaf.endsWith('-turbo') ? '-turbo' : '';
  const candidate = `${base}/reference-to-video${turboSuffix}`;
  return videoModelsWithDescription.some(
    (m) => m.model === candidate && isRefCapable(m),
  );
}

type CatalogueVideoModel = (typeof videoModelsWithDescription)[number];

/**
 * The same family's variant for the other input type, found by model path:
 * either the `text-to-video` segment swapped for `image-to-video` (keeps tier
 * suffixes like `-turbo`, `/fast`, `/turbo` and the vendor prefix, so FLUX 3
 * and FLUX 3 Max, or MiniMax H3 and H3 Max, never cross), or an entry with the
 * very same model id (Veo, Gemini Omni, Grok serve both inputs from one id).
 * A start image alone wants the plain variant, so a First-Last Frame entry
 * that shares the path only wins when nothing else matches; `tail` asks for
 * the entry that takes an end image instead (Veo 3.1 Image -> Veo 3.1
 * First-Last Frame, which shares the id; Seedance 1.5 Pro shares the path).
 */
function findInputTypeSibling(
  model: CatalogueVideoModel,
  wanted: 'text' | 'image',
  freeOnly: boolean,
  tail = false,
): CatalogueVideoModel | undefined {
  const [from, to] =
    wanted === 'image'
      ? ['text-to-video', 'image-to-video']
      : ['image-to-video', 'text-to-video'];
  const swapped = model.model.includes(from)
    ? model.model.replace(from, to)
    : null;
  const candidates = videoModelsWithDescription.filter(
    (m) =>
      m.id !== model.id &&
      (m.model === swapped || m.model === model.model) &&
      m.options.inputType.includes(wanted) &&
      !m.options.requiresReferenceImages &&
      !m.options.isMotionControl &&
      !('comingSoon' in m && m.comingSoon) &&
      (!freeOnly || m.freeUserAccess) &&
      (!tail || m.options.supportsTailImage === true),
  );
  if (tail) return candidates[0];
  return candidates.find((m) => !m.options.supportsTailImage) ?? candidates[0];
}

/**
 * The image-to-video variant of a text model's family (FLUX 3 Max -> FLUX 3
 * Max Image). The Video Generator switches to it when a start image is added,
 * instead of the first image model in the catalogue.
 */
export function findImageSiblingForVideoModel(
  model: CatalogueVideoModel,
  { freeOnly = false }: { freeOnly?: boolean } = {},
): CatalogueVideoModel | undefined {
  return findInputTypeSibling(model, 'image', freeOnly);
}

/**
 * The text-to-video variant of an image model's family (FLUX 3 Max Image ->
 * FLUX 3 Max), restored when the start image is removed.
 */
export function findTextSiblingForVideoModel(
  model: CatalogueVideoModel,
  { freeOnly = false }: { freeOnly?: boolean } = {},
): CatalogueVideoModel | undefined {
  return findInputTypeSibling(model, 'text', freeOnly);
}

/**
 * The same family's entry that takes an end image (Veo 3.1 Image -> Veo 3.1
 * First-Last Frame). The Video Generator switches to it when an end image is
 * added to a model that can't take one, instead of the first tail-capable
 * model in the catalogue. Works from a text model too (its image variant's
 * path), for a deep link that brings both frames at once.
 */
export function findTailImageSiblingForVideoModel(
  model: CatalogueVideoModel,
  { freeOnly = false }: { freeOnly?: boolean } = {},
): CatalogueVideoModel | undefined {
  return findInputTypeSibling(model, 'image', freeOnly, true);
}
